/** Backend API client. */

import { API_URL, apiFetch } from "@/lib/api-client";
import type {
	ChatActivityEntry,
	ChatListEntry,
	ChatEffort,
	ChatMessageAppendDto,
	ChatMessageDto,
	ChatMetadata,
	ChatMonitor,
	PermissionMode,
	ChatSubagentRun,
	ChatWorkflowRun,
	SubagentRunStatus,
	SubagentRunDetail,
	WorkflowRunStatus,
	ChatSendResultDto,
	QueuedChatMessageDto,
	RetractedMessage,
} from "@/lib/chat";
import type { ImagePlaceholderTarget } from "@/lib/chat-files";
import type { KnowledgeGraph, KnowledgeGraphNode } from "@/lib/knowledge-graph";
import { readNewChatPermissionMode } from "@/lib/chat-permission-mode";
import type { CompletionKind } from "@/lib/terminal-completion";
import type { MfaChallenge } from "@/lib/mfa-api";
import type { PasswordCharacterRequirements } from "@/lib/password-policy";
import type { RunnerRegistrationStatus } from "@/lib/runner";

/**
 * HTTP ステータスをそのまま持ち回るエラー。再試行してよい応答かの判定など、呼び出し側が
 * ステータスで分岐する必要がある箇所で使う (メッセージ文字列からの復元は文言変更で黙って壊れる)。
 */
export class ApiStatusError extends Error {
	constructor(
		message: string,
		readonly status: number,
	) {
		super(message);
		this.name = "ApiStatusError";
	}
}

/** 失敗応答の本文から表示用メッセージを取り出す (`error` → ProblemDetails の `title` → fallback)。 */
export async function apiErrorMessage(response: Response, fallback: string): Promise<string> {
	const body: unknown = await response.json().catch(() => null);
	const data =
		typeof body === "object" && body !== null ? (body as { error?: unknown; title?: unknown }) : {};
	return (
		(typeof data.error === "string" && data.error) ||
		(typeof data.title === "string" && data.title) ||
		`${fallback} (${response.status})`
	);
}

export class TerminalApiError extends Error {
	constructor(
		message: string,
		readonly status: number,
	) {
		super(message);
		this.name = "TerminalApiError";
	}
}

export interface WorkspaceSearchSpanDto {
	start: number;
	end: number;
}

export interface WorkspaceSearchMatchDto {
	lineNumber: number;
	lineText: string;
	spans: WorkspaceSearchSpanDto[];
}

export interface WorkspaceSearchFileResultDto {
	path: string;
	matches: WorkspaceSearchMatchDto[];
}

export interface WorkspaceSearchResult {
	files: WorkspaceSearchFileResultDto[];
	matchCount: number;
	truncated: boolean;
}

/** POST /api/environments/{environmentId}/search — 稼働中の環境ワークスペースを rg で検索する。 */
export async function searchWorkspace(
	environmentId: string,
	query: string,
	caseSensitive: boolean,
): Promise<WorkspaceSearchResult> {
	const response = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/search`, {
		method: "POST",
		body: JSON.stringify({ query, caseSensitive }),
	});
	if (!response.ok) {
		const body = (await response.json().catch(() => ({}))) as { error?: string };
		throw new Error(body.error ?? "検索できませんでした。");
	}
	return (await response.json()) as WorkspaceSearchResult;
}

export interface HelpAgentMessage {
	role: "user" | "assistant";
	text: string;
}

/** ヘルプエージェントへ会話履歴を送り、回答本文を受け取る。 */
export async function postHelpAgentMessage(
	messages: HelpAgentMessage[],
	currentPath: string,
): Promise<string> {
	const response = await apiFetch("/api/help-agent/messages", {
		method: "POST",
		body: JSON.stringify({ messages, currentPath }),
	});
	if (!response.ok)
		throw new ApiStatusError(
			await apiErrorMessage(response, "回答を取得できませんでした。"),
			response.status,
		);
	return ((await response.json()) as { text: string }).text;
}

export interface TerminalSessionResult {
	cwd: string | null;
}

/** タブ単位で保持されているターミナルの現在の作業ディレクトリを取得する。 */
export async function fetchTerminalSession(
	environmentId: string,
	chatId: string | null,
	terminalId: string,
	signal?: AbortSignal,
): Promise<TerminalSessionResult> {
	const query = new URLSearchParams({ terminalId });
	if (chatId !== null) query.set("chatId", chatId);
	const response = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/terminal/session?${query}`,
		{ signal },
	);
	if (!response.ok) {
		throw new TerminalApiError("作業ディレクトリを取得できませんでした。", response.status);
	}
	return (await response.json()) as TerminalSessionResult;
}

export interface TerminalCompletionResult {
	candidates: string[];
}

/** タブ単位の作業ディレクトリを基準にファイル名またはコマンド名の補完候補を取得する。 */
export async function fetchTerminalCompletions(
	environmentId: string,
	chatId: string | null,
	terminalId: string,
	word: string,
	kind: CompletionKind,
): Promise<TerminalCompletionResult> {
	const query = new URLSearchParams({ terminalId, word, kind });
	if (chatId !== null) query.set("chatId", chatId);
	const response = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/terminal/completions?${query}`,
	);
	if (!response.ok) {
		throw new TerminalApiError("補完候補を取得できませんでした。", response.status);
	}
	return (await response.json()) as TerminalCompletionResult;
}

/**
 * ワークスペースファイル用 presign-upload の応答。mode が "multipart" のときは uploadUrl が無く、
 * partSizeBytes 区切りのパートを presign-part で発行した URL へ順に PUT する。
 */
interface PresignEnvironmentUploadResult {
	mode: "single" | "multipart";
	uploadToken: string;
	uploadUrl: string | null;
	partSizeBytes: number | null;
}

/** complete-upload の応答。取り込みは非同期のため、この ID で状態をポーリングする。 */
export interface CompleteUploadResult {
	importId: string;
}

/** パート 1 件の PUT が失敗したときの再試行回数 (URL は毎回発行し直す)。 */
const PART_UPLOAD_MAX_ATTEMPTS = 3;

/** 取り込み状態のポーリング間隔 (ミリ秒)。 */
const IMPORT_POLL_INTERVAL_MS = 2000;

/**
 * 取り込み待ちポーリングで連続して許容する一時的な失敗 (ネットワーク断・5xx) の回数。
 * 10GB 級の取り込みは数分かかり、Wi-Fi の瞬断やバックエンドのローリング再起動を跨ぎやすい。
 */
const IMPORT_POLL_MAX_CONSECUTIVE_FAILURES = 5;

/** ~ からのコピーの待ちで許容する連続の失敗 (約 5 分)。コピーは最長 1 時間なので、スリープや数分の切断を跨いでも待ち続ける。 */
const HOME_COPY_POLL_MAX_CONSECUTIVE_FAILURES = 150;

/** エラー応答のボディから表示用メッセージを取り出す。JSON の { error } 形式を優先する。 */
function extractErrorMessage(text: string): string {
	try {
		const parsed: unknown = JSON.parse(text);
		if (
			parsed !== null &&
			typeof parsed === "object" &&
			"error" in parsed &&
			typeof parsed.error === "string"
		) {
			return parsed.error;
		}
	} catch {
		// JSON でないボディはそのまま表示する。
	}
	return text;
}

/**
 * presigned URL へ本体 (ファイル全体または 1 パート) を PUT する。fetch では本体送信の進捗を
 * 観測できないため XHR を使い、<paramref name="onLoadedBytes"/> に送信済みバイト数を通知する。
 * presigned URL への PUT は cookie を付けない (ワイルドカード CORS + credentials は拒否される)。
 * presign に渡した Content-Type が署名に含まれるため、同じ値を送る必要がある。
 */
function putUploadToUrl(
	uploadUrl: string,
	body: Blob,
	contentType: string,
	onLoadedBytes?: (loadedBytes: number) => void,
): Promise<void> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open("PUT", uploadUrl);
		xhr.setRequestHeader("Content-Type", contentType);
		xhr.upload.onprogress = (e) => {
			if (e.lengthComputable) {
				onLoadedBytes?.(e.loaded);
			}
		};
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve();
			} else {
				reject(new Error(`アップロードに失敗しました: ${xhr.status}: ${xhr.responseText}`));
			}
		};
		xhr.onerror = () =>
			reject(new Error("アップロードに失敗しました: ネットワークエラーが発生しました"));
		xhr.send(body);
	});
}

/** multipart の 1 パート分の署名付き PUT URL を発行する。 */
async function presignEnvironmentUploadPart(
	environmentId: string,
	uploadToken: string,
	partNumber: number,
): Promise<string> {
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/presign-part`,
		{
			method: "POST",
			body: JSON.stringify({ uploadToken, partNumber }),
		},
	);
	if (!res.ok) {
		throw new Error(
			`アップロードに失敗しました: ${res.status}: ${extractErrorMessage(await res.text())}`,
		);
	}
	const { url } = (await res.json()) as { url: string };
	return url;
}

/**
 * ファイルを partSizeBytes 区切りのパートに分割して順に PUT する。パート URL は失効
 * (バックエンドの一時クレデンシャル切れ) に備え、送信の直前に 1 つずつ発行する。
 * 失敗したパートは URL を発行し直して再試行し、それでも失敗したら投げる。
 */
async function putMultipartParts(
	file: File,
	environmentId: string,
	uploadToken: string,
	partSizeBytes: number,
	contentType: string,
	onLoadedBytes: (loadedBytes: number) => void,
): Promise<void> {
	const partCount = Math.max(1, Math.ceil(file.size / partSizeBytes));
	for (let partNumber = 1; partNumber <= partCount; partNumber++) {
		const start = (partNumber - 1) * partSizeBytes;
		const part = file.slice(start, Math.min(start + partSizeBytes, file.size));
		for (let attempt = 1; ; attempt++) {
			try {
				const url = await presignEnvironmentUploadPart(environmentId, uploadToken, partNumber);
				await putUploadToUrl(url, part, contentType, (loaded) => onLoadedBytes(start + loaded));
				break;
			} catch (err) {
				if (attempt >= PART_UPLOAD_MAX_ATTEMPTS) throw err;
			}
		}
		onLoadedBytes(start + part.size);
	}
}

/**
 * ファイルをワークスペースの指定パスへ presigned URL でアップロードする。
 * presign → S3 へ直接 PUT (しきい値超えは multipart で分割) → 完了通知、の 3 段階。
 * 完了通知はワークスペースへの取り込みを予約して即座に返るため、取り込みの完了は
 * 返却された importId を waitForEnvironmentImport で待つ。
 * <paramref name="onProgress"/> には本体 PUT の進捗 (0〜100) が通知される。
 * <paramref name="sharedVolumeId"/> を渡すと共有ボリュームの写しに置く。
 */
export async function uploadEnvironmentFile(
	file: File,
	environmentId: string,
	relativePath: string,
	onProgress?: (percent: number) => void,
	sharedVolumeId?: string,
): Promise<CompleteUploadResult> {
	const contentType = file.type || "application/octet-stream";

	const presignRes = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/presign-upload`,
		{
			method: "POST",
			body: JSON.stringify({
				relativePath,
				contentType,
				sizeBytes: file.size,
				sharedVolumeId,
			}),
		},
	);
	if (!presignRes.ok) {
		throw new Error(
			`アップロードに失敗しました: ${presignRes.status}: ${extractErrorMessage(await presignRes.text())}`,
		);
	}
	const presign = (await presignRes.json()) as PresignEnvironmentUploadResult;

	const reportLoadedBytes = (loadedBytes: number) => {
		if (file.size > 0) {
			onProgress?.(Math.min(100, Math.round((loadedBytes / file.size) * 100)));
		}
	};

	try {
		if (presign.mode === "multipart" && presign.partSizeBytes) {
			await putMultipartParts(
				file,
				environmentId,
				presign.uploadToken,
				presign.partSizeBytes,
				contentType,
				reportLoadedBytes,
			);
		} else {
			if (!presign.uploadUrl) {
				throw new Error("アップロードに失敗しました: サーバの応答にアップロード先がありません");
			}
			await putUploadToUrl(presign.uploadUrl, file, contentType, reportLoadedBytes);
		}

		const completeRes = await apiFetch(
			`/api/environments/${encodeURIComponent(environmentId)}/files/complete-upload`,
			{
				method: "POST",
				body: JSON.stringify({ uploadToken: presign.uploadToken }),
			},
		);
		if (!completeRes.ok) {
			throw new Error(
				`アップロードに失敗しました: ${completeRes.status}: ${extractErrorMessage(await completeRes.text())}`,
			);
		}

		return (await completeRes.json()) as CompleteUploadResult;
	} catch (err) {
		// 取り込まれなかった実体がストレージに残らないよう破棄を依頼してから、元のエラーを送出する。
		await abortEnvironmentUpload(environmentId, presign.uploadToken).catch((abortErr) => {
			// 破棄依頼まで失敗しても、ユーザーに見せるべきは元のアップロードエラー。残った実体は
			// バックエンドの孤児回収スイープが必ず回収するため、ここでは記録にとどめる。
			console.error("アップロードの破棄依頼に失敗しました", abortErr);
		});
		throw err;
	}
}

/**
 * 非同期取り込みの完了をポーリングで待つ。取り込みが失敗した場合はサーバのエラーメッセージで投げる。
 */
export async function waitForEnvironmentImport(
	environmentId: string,
	importId: string,
): Promise<void> {
	await waitForBackgroundTask(
		`/api/environments/${encodeURIComponent(environmentId)}/files/imports/${encodeURIComponent(importId)}`,
		"取り込み状態の取得に失敗しました",
		(error) => new Error(error ?? "ワークスペースへの取り込みに失敗しました。"),
		IMPORT_POLL_MAX_CONSECUTIVE_FAILURES,
	);
}

/** 取り込み (pending・importing) と ~ からのコピー (pending・copying) の状態。 */
interface BackgroundTaskStatus {
	status: "pending" | "importing" | "copying" | "ready" | "failed";
	error: string | null;
}

/**
 * サーバの裏の処理 (status が ready / failed になる) の完了をポーリングで待つ。failed は toFailure にサーバのエラーメッセージを渡して投げる。
 * ネットワーク断や 5xx は一時的とみなし、連続 maxConsecutiveFailures 回までポーリングを継続する (サーバ側の処理は進んでいるため、
 * 瞬断だけで失敗扱いにしない)。4xx は恒久的な失敗として即座に投げる。
 */
async function waitForBackgroundTask(
	url: string,
	statusErrorPrefix: string,
	toFailure: (error: string | null | undefined) => Error,
	maxConsecutiveFailures: number,
): Promise<void> {
	let consecutiveFailures = 0;
	for (;;) {
		let res: Response | null = null;
		try {
			res = await apiFetch(url);
		} catch (err) {
			consecutiveFailures++;
			if (consecutiveFailures >= maxConsecutiveFailures) throw err;
		}

		if (res !== null) {
			if (res.ok) {
				consecutiveFailures = 0;
				const status = (await res.json()) as BackgroundTaskStatus;
				if (status.status === "ready") return;
				if (status.status === "failed") {
					throw toFailure(status.error);
				}
			} else if (res.status >= 500) {
				consecutiveFailures++;
				if (consecutiveFailures >= maxConsecutiveFailures) {
					throw new Error(
						`${statusErrorPrefix}: ${res.status}: ${extractErrorMessage(await res.text())}`,
					);
				}
			} else {
				throw new Error(
					`${statusErrorPrefix}: ${res.status}: ${extractErrorMessage(await res.text())}`,
				);
			}
		}

		await new Promise((resolve) => setTimeout(resolve, IMPORT_POLL_INTERVAL_MS));
	}
}

/** 中断したアップロードをサーバに通知し、S3 に残った実体を破棄させる。 */
async function abortEnvironmentUpload(environmentId: string, uploadToken: string): Promise<void> {
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/abort-upload`,
		{
			method: "POST",
			body: JSON.stringify({ uploadToken }),
		},
	);
	if (!res.ok) {
		throw new Error(`アップロードの破棄に失敗しました: ${res.status}: ${await res.text()}`);
	}
}

/** バックエンド EnvironmentFileDto に対応。path は環境ワークスペースルートからの相対パス。 */
export interface EnvironmentFileDto {
	id: string;
	environmentId: string;
	path: string;
	isDirectory: boolean;
	contentType: string;
	sizeBytes: number;
	updatedAt: string;
	/**
	 * 行の版番号 (PostgreSQL の xmin)。行が更新されるたびに必ず変わる。上書き保存の楽観ロックに使う。
	 * 表示や変更検知には壁時計の `updatedAt` を使う。
	 */
	version: number;
	/** 共有ボリュームの写しのファイル (path は環境の中の絶対パス) なら true。 */
	readOnly?: boolean;
}

/**
 * バックエンド EnvironmentEntryDto に対応。ツリーの遅延読み込みで返る 1 エントリ。
 * 索引はファイルを含むディレクトリの行を持たない (パスの集約で導出する) ため、ディレクトリでは
 * `id` / `contentType` / `updatedAt` / `version` が null、`sizeBytes` は 0 になる。
 */
export interface EnvironmentEntryDto {
	id: string | null;
	path: string;
	isDirectory: boolean;
	contentType: string | null;
	sizeBytes: number;
	updatedAt: string | null;
	version: number | null;
}

/** あるディレクトリ直下のエントリ一覧。`hasMore` が true なら offset を進めて続きを取得する。 */
export interface EnvironmentFileChildrenDto {
	entries: EnvironmentEntryDto[];
	hasMore: boolean;
}

/** フォルダ作成先に同名のエントリが存在することを表す。 */
export class EnvironmentFolderConflictError extends Error {
	constructor() {
		super("同名のエントリが既に存在します。");
		this.name = "EnvironmentFolderConflictError";
	}
}

/**
 * POST /api/environments/{environmentId}/files/create-folder
 * ワークスペースの指定相対パスに空フォルダを作成する。
 */
export async function createEnvironmentFolder(
	environmentId: string,
	relativePath: string,
): Promise<EnvironmentFileDto> {
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/create-folder`,
		{
			method: "POST",
			body: JSON.stringify({ relativePath }),
		},
	);
	if (!res.ok) {
		if (res.status === 409) {
			throw new EnvironmentFolderConflictError();
		}
		const responseText = await res.text();
		throw new Error(`フォルダの作成に失敗しました: ${res.status}: ${responseText}`);
	}
	return (await res.json()) as EnvironmentFileDto;
}

/**
 * POST /api/environments/{environmentId}/files/delete
 * ワークスペース内のファイルまたはフォルダを削除する。索引に存在しなかったパスは無視されるため、
 * 戻り値の deletedCount はリクエストした件数より少ない場合がある。
 */
export async function deleteEnvironmentEntries(
	environmentId: string,
	paths: readonly string[],
): Promise<{ deletedCount: number }> {
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/delete`,
		{
			method: "POST",
			body: JSON.stringify({ paths }),
		},
	);
	if (!res.ok) {
		const responseText = await res.text();
		throw new Error(`削除に失敗しました: ${res.status}: ${responseText}`);
	}
	return (await res.json()) as { deletedCount: number };
}

/**
 * POST /api/environments/{environmentId}/files/create-file
 * ワークスペースの指定相対パスに空ファイルを作成する。
 */
export async function createEnvironmentFile(
	environmentId: string,
	relativePath: string,
): Promise<EnvironmentFileDto> {
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/create-file`,
		{
			method: "POST",
			body: JSON.stringify({ relativePath }),
		},
	);
	if (!res.ok) {
		if (res.status === 409) {
			throw new Error("同名のエントリが既に存在します。");
		}
		const responseText = await res.text();
		throw new Error(`ファイルの作成に失敗しました: ${res.status}: ${responseText}`);
	}
	return (await res.json()) as EnvironmentFileDto;
}

/**
 * POST /api/environments/{environmentId}/files/move
 * ワークスペース内のエントリ (ファイル / フォルダ) を移動する。
 * sourcePath / destinationPath はワークスペースルートからの相対パス。
 */
export async function moveEnvironmentEntry(
	environmentId: string,
	sourcePath: string,
	destinationPath: string,
): Promise<void> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/files/move`, {
		method: "POST",
		body: JSON.stringify({ sourcePath, destinationPath }),
	});
	if (!res.ok) {
		if (res.status === 409) {
			throw new Error("移動先に同名のエントリが既に存在します。");
		}
		const responseText = await res.text();
		throw new Error(`移動に失敗しました: ${res.status}: ${responseText}`);
	}
}

/**
 * POST /api/environments/{environmentId}/files/download-archive
 * 指定したファイル／フォルダを含む ZIP アーカイブを返す。
 */
export async function downloadEnvironmentFilesArchive(
	environmentId: string,
	paths: string[],
): Promise<Blob> {
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/download-archive`,
		{
			method: "POST",
			body: JSON.stringify({ paths }),
		},
	);
	if (!res.ok) {
		const responseText = await res.text();
		throw new Error(`ZIPファイルのダウンロードに失敗しました: ${res.status}: ${responseText}`);
	}
	return res.blob();
}

/**
 * POST /api/environments/{environmentId}/files/rename
 * ワークスペース内のエントリ (ファイル / フォルダ) を同一ディレクトリ内で名前変更する。
 * path はワークスペースルートからの相対パス、newName はパス区切りを含まない新しい名前。
 */
export async function renameEnvironmentEntry(
	environmentId: string,
	path: string,
	newName: string,
): Promise<void> {
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/rename`,
		{
			method: "POST",
			body: JSON.stringify({ path, newName }),
		},
	);
	if (!res.ok) {
		if (res.status === 409) {
			throw new Error("同名のエントリが既に存在します。");
		}
		const responseText = await res.text();
		throw new Error(`名前の変更に失敗しました: ${res.status}: ${responseText}`);
	}
}

/** バックエンド EnvironmentExtractResponse に対応。isDirectory は単一ファイルの gzip を伸長した場合だけ false。 */
export interface EnvironmentExtractResultDto {
	destinationPath: string;
	isDirectory: boolean;
}

/**
 * POST /api/environments/{environmentId}/files/extract — アーカイブ (zip / tar / tar.gz / gz) を
 * 同じディレクトリ内の拡張子を除いた名前へ展開する。
 */
export async function extractEnvironmentArchive(
	environmentId: string,
	path: string,
): Promise<EnvironmentExtractResultDto> {
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/extract`,
		{
			method: "POST",
			body: JSON.stringify({ path }),
		},
	);
	if (!res.ok) {
		if (res.status === 409) {
			throw new Error("展開先に同名のエントリが既に存在します。");
		}
		// 400 は未対応形式・壊れたアーカイブなど利用者向けの説明を error に載せて返すのでそのまま表示する。
		if (res.status === 400) {
			throw new Error(extractErrorMessage(await res.text()));
		}
		throw new Error(`展開に失敗しました (${res.status})`);
	}
	return (await res.json()) as EnvironmentExtractResultDto;
}

/** バックエンド EnvironmentDto に対応。チャットが属するサンドボックス環境のメタデータ。 */
export interface Environment {
	id: string;
	name: string;
	createdById: string;
	createdAt: string;
	organizationId: string;
	projectId: string;
	organizationName: string;
	/** 外向き通信で許可するドメインの決め方。 */
	domainEgressMode: DomainEgressMode;
	/**
	 * 許可するドメインの一覧。domainEgressMode が "allowList" のときのみ有効
	 * (空配列 = 全遮断)。他モードでは null。
	 */
	allowedDomains: string[] | null;
	/** この環境を使うチャットで最後にメッセージがやり取りされた日時。未使用の場合は null。 */
	lastAccessedAt: string | null;
	/** 作業ツリーを載せるボリュームの使用バイト数。未測定の場合は null。 */
	diskUsedBytes: number | null;
	/** 作業ツリーを載せるボリュームの総容量バイト数。未測定の場合は null。 */
	diskTotalBytes: number | null;
	/** 次回起動時のインスタンスサイズ (前回の起動で選んだもの。未選択なら "s")。 */
	instanceSize: EnvironmentInstanceSize;
	/** 次回の起動でデバイスの GPU を渡すか (選んでいなければ、デバイスが GPU を報告しているか)。クラウドの環境では常に false。 */
	gpu: boolean;
	/** この環境にマウントする共有ボリューム (次回起動時に反映)。 */
	volumeIds: string[];
	/** 作成時に選んだルートボリューム (環境専用 EBS) の容量 (GiB)。作成後は変わらない。デバイスの環境では使わない。 */
	rootVolumeGib: number;
	/** 環境を動かすデバイス。クラウドの環境では null。 */
	runner: EnvironmentRunner | null;
	/** PUT volumes でデバイスの環境にボリュームを付けたときだけ、デバイスに知らせられたか。 */
	sharedVolumeSync?: DeviceSharedVolumeSync;
}

/** デバイスの環境の実行場所。online は一覧を取得した時点の判定 (以後は状態の取得で更新する)。 */
export interface EnvironmentRunner {
	id: string;
	name: string;
	online: boolean;
	/** デバイスが GPU を報告しているか。「GPU を使う」を選べるか。 */
	hasGpu: boolean;
	/** GPU を使えない理由と直し方 (backend が理由のコードから作る固定の文)。GPU があれば null。 */
	gpuMessage: string | null;
	/** デバイスの CPU 数。報告が無ければ null (サイズを制限しない)。 */
	hostCpus: number | null;
	/** デバイスのメモリの総量 (バイト)。報告が無ければ null (サイズを制限しない)。 */
	hostMemoryBytes: number | null;
	/** ランナーが最低の版より古く、更新できる (backend が判定)。 */
	updateRequired: boolean;
}

/**
 * 選べる環境インスタンスサイズ (バックエンドの EnvironmentInstanceSizes.All と同じ順序・識別子)。
 * s / l / xl / xxl は共有ノード、xxxl と gpu* は 1 環境が EC2 を 1 台占有するサイズ。gpu* の英字は
 * GPU の種類、数字はその中で vCPU の昇順に振った連番。xxxxl / xxxxxl はデバイスの環境でだけ選べる。
 */
export const ENVIRONMENT_INSTANCE_SIZES = [
	"s",
	"l",
	"xl",
	"xxl",
	"xxxl",
	"xxxxl",
	"xxxxxl",
	"gpuA1",
	"gpuA2",
	"gpuA3",
	"gpuA4",
	"gpuA5",
	"gpuA6",
	"gpuA7",
	"gpuA8",
	"gpuB1",
	"gpuB2",
	"gpuB3",
	"gpuB4",
	"gpuB5",
	"gpuB6",
	"gpuB7",
	"gpuB8",
	"gpuB9",
	"gpuB10",
	"gpuC1",
	"gpuC2",
	"gpuC3",
	"gpuC4",
	"gpuC5",
	"gpuC6",
	"gpuC7",
	"gpuC8",
	"gpuD1",
	"gpuD2",
	"gpuD3",
	"gpuD4",
	"gpuD5",
	"gpuD6",
	"gpuE1",
	"gpuE2",
	"gpuF1",
	"gpuF2",
	"gpuG1",
	"gpuH1",
	"gpuH2",
	"gpuH3",
	"gpuH4",
	"gpuH5",
	"gpuH6",
	"gpuI1",
] as const;

/** 環境インスタンスのサイズ。Pod のリソース・配置先ノードと稼働単価を決める。 */
export type EnvironmentInstanceSize = (typeof ENVIRONMENT_INSTANCE_SIZES)[number];

/** GPU サイズか。デバイスの環境では選べない。 */
export function isGpuInstanceSize(size: EnvironmentInstanceSize): boolean {
	return size.startsWith("gpu");
}

/** デバイスの環境でだけ選べるサイズ (バックエンドの DeviceOnly)。一覧が届く前の行数を決めるのに使う。 */
export const DEVICE_ONLY_INSTANCE_SIZES: readonly EnvironmentInstanceSize[] = ["xxxxl", "xxxxxl"];

/** サイズの短い表示名 ("s" → "S"、"gpuA1" → "GPU A1"、"xxxxl" → "4XL")。バックエンドの DisplayName と同じ規則。 */
export function formatInstanceSize(size: EnvironmentInstanceSize): string {
	if (isGpuInstanceSize(size)) return `GPU ${size.slice(3)}`;
	const xCount = size.length - 1;
	return xCount >= 4 ? `${xCount}XL` : size.toUpperCase();
}

/** 環境の次回の起動のサイズの表示名。デバイスの環境で GPU を使うなら "XXL + GPU" の形にする。 */
export function formatEnvironmentSize(environment: Environment): string {
	const size = formatInstanceSize(environment.instanceSize);
	return environment.runner && environment.gpu ? `${size} + GPU` : size;
}

/** GPU サイズの顧客向けスペック。EC2 のインスタンス型は含まない。 */
export interface EnvironmentGpuInfo {
	name: string;
	count: number;
	/** 全 GPU の合計メモリ (GB、カタログ値)。 */
	totalMemoryGb: number;
}

/**
 * GET /api/environments/instance-sizes の 1 行。cpuRequest / cpuLimit は Kubernetes の CPU 量
 * ("500m" = 0.5 vCPU)、memory は request = limit の 1 値 ("2Gi")。creditsPerHour は稼働 1 時間あたりの
 * クレジットで、計上時と同じ規則で解決された実際に請求される単価。gpu は GPU サイズのみ非 null。
 * deviceOnly のサイズはデバイスの環境でだけ選べ、単価が無いため creditsPerHour は null。
 */
export type EnvironmentInstanceSizeInfo = {
	size: EnvironmentInstanceSize;
	label: string;
	cpuRequest: string;
	cpuLimit: string;
	memory: string;
	/** 1 環境が EC2 を 1 台占有するサイズ (GPU サイズと XXXL)。その場で起動するため使えるまで数分かかる。 */
	dedicated: boolean;
	gpu: EnvironmentGpuInfo | null;
} & ({ deviceOnly: false; creditsPerHour: number } | { deviceOnly: true; creditsPerHour: null });

/** 起動時に選べるインスタンスサイズの一覧を取得する (組織メンバーなら誰でも参照可)。 */
export async function getEnvironmentInstanceSizes(
	signal?: AbortSignal,
): Promise<EnvironmentInstanceSizeInfo[]> {
	const res = await apiFetch("/api/environments/instance-sizes", { signal });
	if (!res.ok) throw new Error(`インスタンスサイズの取得に失敗しました (${res.status})`);
	return (await res.json()) as EnvironmentInstanceSizeInfo[];
}

/**
 * 1 組織が同時に稼働させられるクラウドの環境インスタンス数の上限。停止中の環境とデバイスの環境は数えない。
 * バックエンドの RunningEnvironmentService.MaxRunningPerOrganization と一致させること。
 */
export const MAX_RUNNING_ENVIRONMENTS_PER_ORG = 20;

/**
 * 1 台のデバイスに作れる環境 (停止中を含む) の数の上限。
 * バックエンドの EnvironmentStore.MaxEnvironmentsPerDevice と一致させること。
 */
export const MAX_ENVIRONMENTS_PER_DEVICE = 4;

/** 稼働中 (= 同時稼働の枠を占有する) とみなす状態。バックエンドの RunningEnvironmentService と揃える。 */
export function isRunningStatus(status: EnvironmentStatus | undefined): boolean {
	return status === "running" || status === "pending" || status === "waitingForCapacity";
}

/** 環境 Pod が存在する (ノードの資源を確保し、共有ボリュームをマウントしている) とみなす状態。 */
export function environmentHasPod(status: EnvironmentStatus | undefined): boolean {
	return (
		status === "running" ||
		status === "pending" ||
		status === "waitingForCapacity" ||
		status === "shuttingDown" ||
		status === "errored"
	);
}

/** GET /api/environments — 組織の環境一覧を作成日時の降順で取得する。 */
export async function listEnvironments(): Promise<Environment[]> {
	const res = await apiFetch("/api/environments");
	if (!res.ok) throw new Error(`エージェントの取得に失敗しました (${res.status})`);
	return (await res.json()) as Environment[];
}

/**
 * ルートボリューム容量 (GiB) の範囲と既定。ダイアログを開いた瞬間から入力と説明を描けるよう
 * フロントにも持つ。バックエンドの EnvironmentRootVolume (MinGib / MaxGib / DefaultGib) と一致させること。
 */
export const ENVIRONMENT_ROOT_VOLUME_GIB = { min: 20, max: 2048, default: 20 } as const;

/**
 * 環境の作成時に選べるルートボリューム容量の範囲・既定と、1 GiB を 1 時間確保したときの
 * クレジット単価 (計上時と同じ規則で解決された実際に請求される単価)。
 */
export interface EnvironmentRootVolumeOptions {
	minGib: number;
	maxGib: number;
	defaultGib: number;
	creditsPerGibHour: number;
}

/** 作成時に選べるルートボリューム容量の範囲と単価を取得する (組織メンバーなら誰でも参照可)。 */
export async function getEnvironmentRootVolumeOptions(
	signal?: AbortSignal,
): Promise<EnvironmentRootVolumeOptions> {
	const res = await apiFetch("/api/environments/root-volume-options", { signal });
	if (!res.ok) throw new Error(`ルートボリュームの選択肢の取得に失敗しました (${res.status})`);
	return (await res.json()) as EnvironmentRootVolumeOptions;
}

/**
 * POST /api/environments — 呼び出し元の組織のプロジェクトに新しい環境を作成する。
 * 設定 (許可ドメイン) は既定値で作成される。名前重複時と、デバイスに作れる数
 * (MAX_ENVIRONMENTS_PER_DEVICE) に達しているときは 409 のメッセージを投げる。クラウドの環境の作成数に上限はない。
 * rootVolumeGib はルートボリュームの容量 (GiB) で、作成後は変えられない。
 * runnerId を指定するとデバイスで動く環境を作る (組織の管理者だけ)。デバイスの環境では rootVolumeGib を送らない。
 */
export async function createEnvironment(input: {
	name: string;
	projectId: string;
	rootVolumeGib?: number;
	runnerId?: string;
}): Promise<Environment> {
	const res = await apiFetch("/api/environments", {
		method: "POST",
		body: JSON.stringify(input),
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? "このエージェント名はすでに使用されています。");
	}
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? data.title ?? `エージェントの作成に失敗しました (${res.status})`);
	}
	return (await res.json()) as Environment;
}

/**
 * POST /api/environments/{environmentId}/root-volume — ルートボリュームを rootVolumeGib (GiB) へ拡張する。
 * 現在より大きい値のみ受け付ける (EBS は縮小できないため元に戻せない)。範囲外・縮小は 400 のメッセージを投げる。
 */
export async function expandEnvironmentRootVolume(
	environmentId: string,
	rootVolumeGib: number,
): Promise<Environment> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/root-volume`, {
		method: "POST",
		body: JSON.stringify({ rootVolumeGib }),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(
			data.error ?? data.title ?? `ルートボリュームの拡張に失敗しました (${res.status})`,
		);
	}
	return (await res.json()) as Environment;
}

/**
 * POST /api/environments/{environmentId} — 環境の名前を変更する。
 * 名前重複時は 409 のメッセージを投げる。
 */
export async function updateEnvironment(environmentId: string, name: string): Promise<Environment> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}`, {
		method: "POST",
		body: JSON.stringify({ name }),
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? "このエージェント名はすでに使用されています。");
	}
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `エージェント名の変更に失敗しました (${res.status})`);
	}
	return (await res.json()) as Environment;
}

/**
 * 環境コンテナの起動状態。running = 起動中、pending = 起動準備中、
 * waitingForCapacity = GPU インスタンスの在庫切れで確保できるまで再試行中 (停止で取り消せる)、
 * shuttingDown = 終了処理中、errored = 異常だが Pod は存在する (停止してから起動し直す)、
 * startupFailed = 起動失敗として回収済みで Pod は無い (起動で再試行する)、notRunning = 停止中または未作成。
 */
export type EnvironmentStatus =
	| "running"
	| "pending"
	| "waitingForCapacity"
	| "shuttingDown"
	| "errored"
	| "startupFailed"
	| "notRunning";

/**
 * 異常の原因。outOfMemory = メモリ上限超過による強制終了、unschedulable = 環境を載せられる
 * ノードが無く起動できない (共有ノード)、capacityUnavailable = 選択した GPU サイズの専有
 * インスタンスを確保できない (他の環境を止めても解消しない)、containerUnavailable = それ以外の異常。
 */
export type SandboxErrorReason =
	| "containerUnavailable"
	| "outOfMemory"
	| "unschedulable"
	| "capacityUnavailable"
	| "diskLimitExceeded"
	| "diskSpaceInsufficient"
	| "deviceAccessDenied"
	| "volumeExpansionRejected";

/**
 * バックエンド EnvironmentStatusDto に対応。errorReason は status が "errored" または
 * "startupFailed" のときのみ設定される。
 */
export interface EnvironmentStatusEntry {
	environmentId: string;
	status: EnvironmentStatus;
	errorReason?: SandboxErrorReason;
	/** 起動の失敗の具体的な説明 (deviceAccessDenied ならどの機器か)。無ければ null。 */
	failureDetail?: string | null;
	/** デバイスの環境のランナーのオンライン判定。クラウドの環境では無い。 */
	runnerOnline?: boolean;
	/** サーバーの保守 (ノードの入れ替え) で環境が再起動される期限 (ISO 8601)。予定が無ければ null。 */
	scheduledRestartAt?: string | null;
}

/**
 * GET /api/environments/statuses — 自組織の各環境コンテナの起動状態を取得する。
 * プロジェクトページで一覧表示後に非同期取得・ポーリングして各カードへ反映する想定。
 */
export async function listEnvironmentStatuses(
	signal?: AbortSignal,
): Promise<EnvironmentStatusEntry[]> {
	const res = await apiFetch("/api/environments/statuses", { signal });
	if (!res.ok) throw new Error(`エージェントの状態取得に失敗しました (${res.status})`);
	return (await res.json()) as EnvironmentStatusEntry[];
}

/**
 * GET /api/environments/{environmentId}/status — 単一環境コンテナの起動状態を取得する。
 * start / stop 後に遷移完了までポーリングする用途。
 */
export async function getEnvironmentStatus(
	environmentId: string,
	signal?: AbortSignal,
): Promise<EnvironmentStatusEntry> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/status`, {
		signal,
	});
	if (!res.ok) throw new Error(`エージェントの状態取得に失敗しました (${res.status})`);
	return (await res.json()) as EnvironmentStatusEntry;
}

/**
 * startEnvironment のオプション。instanceSize を渡すと次回以降の起動サイズとして保存したうえで起動する。
 * gpu (デバイスの環境だけ) も同じく、渡すと保存したうえで起動する。
 */
export interface StartEnvironmentOptions {
	instanceSize?: EnvironmentInstanceSize;
	gpu?: boolean;
	signal?: AbortSignal;
}

/**
 * POST /api/environments/{environmentId}/start — 環境コンテナの起動を要求する (202)。
 * 起動完了は待たないため、進捗は getEnvironmentStatus のポーリングで追う。
 * instanceSize を省略すると環境に保存済みのサイズで起動する (再起動と同じ)。
 * 組織の同時稼働上限に達している場合と、プロジェクトがアーカイブ済みの場合は 409 が、選べないサイズなどは 400 が返り、
 * その理由をエラーメッセージとして投げる。
 */
export async function startEnvironment(
	environmentId: string,
	options: StartEnvironmentOptions = {},
): Promise<void> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/start`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		// gpu は省くと保存済みの値を保つ。
		body: JSON.stringify({ instanceSize: options.instanceSize ?? null, gpu: options.gpu }),
		signal: options.signal,
	});
	if (res.status === 400 || res.status === 409) {
		// JSON でない本文は汎用の文に落とす。本文の読み込みの中断・通信の失敗はそのまま投げる。
		const data: { error?: unknown } = await res.json().catch((err: unknown) => {
			if (err instanceof SyntaxError) return {};
			throw err;
		});
		if (typeof data.error === "string") throw new Error(data.error);
		if (res.status === 409) {
			throw new Error(
				`同時に稼働できるエージェントは1組織につき最大${MAX_RUNNING_ENVIRONMENTS_PER_ORG}個までです。`,
			);
		}
	}
	if (!res.ok) throw new Error(`エージェントの起動に失敗しました (${res.status})`);
}

export interface EnvironmentStopResult {
	/** 停止の後に共有ボリュームへの反映を頼んだか (デバイスの環境で、付いているボリュームがあり、ランナーが対応しているとき)。 */
	sharedVolumesPushRequested?: boolean;
	hookStatus:
		| "notApplicable"
		| "notRunning"
		| "notConfigured"
		| "succeeded"
		| "failed"
		| "timedOut"
		| "unconfirmed";
	stopConfirmed: boolean;
	notice: string | null;
}

/**
 * POST /api/environments/{environmentId}/restart — 環境コンテナの再起動を要求する (202)。
 * 停止要求までを済ませてから返り、終了完了待ちと起動はサーバー側で行うため、
 * ページを離れても再起動は完遂される。進捗は getEnvironmentStatus のポーリングで追う。
 * 実行中のエージェントジョブがある場合と、起動が拒否される状況 (アーカイブ済みなど) は
 * 409 が返り、その理由をエラーメッセージとして投げる。
 */
export async function restartEnvironment(
	environmentId: string,
	signal?: AbortSignal,
): Promise<EnvironmentStopResult> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/restart`, {
		method: "POST",
		signal,
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new ApiStatusError(data.error ?? "エージェントを再起動できません。", res.status);
	}
	if (!res.ok) throw new Error(`エージェントの再起動に失敗しました (${res.status})`);
	return (await res.json()) as EnvironmentStopResult;
}

/**
 * POST /api/environments/{environmentId}/stop — 環境コンテナを停止する。
 * 実行中のエージェントジョブがある場合は 409 が返り、その理由をエラーメッセージとして投げる。
 */
export async function stopEnvironment(
	environmentId: string,
	options: { pushSharedVolumes?: boolean } = {},
): Promise<EnvironmentStopResult> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/stop`, {
		method: "POST",
		...(options.pushSharedVolumes === undefined
			? {}
			: { body: JSON.stringify({ pushSharedVolumes: options.pushSharedVolumes }) }),
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? "実行中のエージェントがあるため停止できません。");
	}
	if (!res.ok) throw new Error(`エージェントの停止に失敗しました (${res.status})`);
	return (await res.json()) as EnvironmentStopResult;
}

/**
 * DELETE /api/environments/{environmentId} — 環境を完全に削除する。
 * 環境内のファイル・チャット履歴もすべて削除され、元に戻せない。
 * 実行中のエージェントジョブがある場合は 409 が返り、その理由をエラーメッセージとして投げる。
 */
export async function deleteEnvironment(environmentId: string): Promise<void> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}`, {
		method: "DELETE",
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? "実行中のエージェントがあるため削除できません。");
	}
	if (!res.ok) throw new Error(`エージェントの削除に失敗しました (${res.status})`);
}

/** バックエンド ProjectDto に対応。チャットと環境をまとめる組織単位のコンテナ。 */
export interface Project {
	id: string;
	name: string;
	createdById: string;
	createdAt: string;
	updatedAt: string;
	/** アーカイブ (ソフト削除) された日時。有効なプロジェクトは null。 */
	archivedAt: string | null;
}

/** GET /api/projects のクエリ。省略時は自組織の有効なプロジェクトのみ。 */
export interface ListProjectsOptions {
	/** アーカイブ済みのプロジェクトも含めるか。 */
	includeArchived?: boolean;
}

/**
 * GET /api/projects — 自組織のプロジェクト一覧を作成日時の降順で取得する。
 * アーカイブ済みを含めた場合、有効なプロジェクトの後ろに並ぶ。
 */
export async function listProjects(options: ListProjectsOptions = {}): Promise<Project[]> {
	const query = options.includeArchived ? "?includeArchived=true" : "";
	const res = await apiFetch(`/api/projects${query}`);
	if (!res.ok) throw new Error(`プロジェクトの取得に失敗しました (${res.status})`);
	return (await res.json()) as Project[];
}

/** POST /api/projects — 呼び出し元の組織にプロジェクトを作成する。名前重複時は 409 のメッセージを投げる。 */
export async function createProject(name: string): Promise<Project> {
	const res = await apiFetch("/api/projects", {
		method: "POST",
		body: JSON.stringify({ name }),
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(
			data.error ?? "このプロジェクト名はすでに使用されています (アーカイブ済みを含む)。",
		);
	}
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `プロジェクトの作成に失敗しました (${res.status})`);
	}
	return (await res.json()) as Project;
}

/** POST /api/projects/{projectId} — プロジェクト名を更新する。名前重複時は 409 のメッセージを投げる。 */
export async function updateProject(projectId: string, name: string): Promise<Project> {
	const res = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}`, {
		method: "POST",
		body: JSON.stringify({ name }),
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(
			data.error ?? "このプロジェクト名はすでに使用されています (アーカイブ済みを含む)。",
		);
	}
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `プロジェクトの更新に失敗しました (${res.status})`);
	}
	return (await res.json()) as Project;
}

/**
 * DELETE /api/projects/{projectId} — プロジェクトをアーカイブ (ソフト削除) する。
 * 稼働中の環境インスタンスがある場合は 409 が返り、その理由をエラーメッセージとして投げる。
 */
export async function deleteProject(projectId: string): Promise<void> {
	const res = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}`, {
		method: "DELETE",
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? "稼働中のエージェントがあるためアーカイブできません。");
	}
	if (!res.ok) throw new Error(`プロジェクトの削除に失敗しました (${res.status})`);
}

/**
 * POST /api/projects/{projectId}/unarchive — アーカイブ済みプロジェクトを復元する。
 * プロジェクト名はアーカイブ済みも含めて組織内で一意なため、復元時に名前が衝突することはない。
 */
export async function unarchiveProject(projectId: string): Promise<Project> {
	const res = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/unarchive`, {
		method: "POST",
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `プロジェクトの復元に失敗しました (${res.status})`);
	}
	return (await res.json()) as Project;
}

/**
 * GET /api/projects/{projectId}/members — プロジェクトメンバーのユーザー ID を
 * 追加日時順で取得する (管理者のみ)。
 */
export async function getProjectMembers(projectId: string): Promise<string[]> {
	const res = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/members`);
	if (!res.ok) throw new Error(`メンバーの取得に失敗しました (${res.status})`);
	const data = (await res.json()) as { userIds: string[] };
	return data.userIds;
}

/**
 * PUT /api/projects/{projectId}/members — プロジェクトメンバーを指定リストで全置換する
 * (管理者のみ)。保存後のメンバー ID リストを返す。
 */
export async function replaceProjectMembers(
	projectId: string,
	userIds: string[],
): Promise<string[]> {
	const res = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/members`, {
		method: "PUT",
		body: JSON.stringify({ userIds }),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `メンバーの保存に失敗しました (${res.status})`);
	}
	const data = (await res.json()) as { userIds: string[] };
	return data.userIds;
}

/**
 * 外向き通信で許可するドメインの決め方。"allowAll" は制限なし (すべて許可)、
 * "allowDefault" は組み込み既定リスト、"allowList" は allowedDomains の明示リストを使う。
 */
export type DomainEgressMode = "allowAll" | "allowDefault" | "allowList";

/** 環境の起動時設定の更新で指定する内容。 */
export interface EnvironmentSettingsInput {
	/** 外向き通信で許可するドメインの決め方。 */
	domainEgressMode: DomainEgressMode;
	/** "allowList" のときのみ有効な許可ドメインの一覧。他モードでは無視される。 */
	allowedDomains: string[] | null;
}

/**
 * GET /api/environments/default-allowed-domains —
 * 許可ドメインを指定しない環境に適用される組み込み既定リストを取得する。
 */
export async function getDefaultAllowedDomains(): Promise<string[]> {
	const res = await apiFetch("/api/environments/default-allowed-domains");
	if (!res.ok) throw new Error(`既定の許可ドメインの取得に失敗しました (${res.status})`);
	const data = (await res.json()) as { domains: string[] };
	return data.domains;
}

/**
 * POST /api/environments/{environmentId}/settings — 環境の起動時設定を全て置き換える。
 * 変更は次回の起動から反映される。
 */
export async function updateEnvironmentSettings(
	environmentId: string,
	input: EnvironmentSettingsInput,
): Promise<Environment> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/settings`, {
		method: "POST",
		body: JSON.stringify(input),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(
			data.error ?? data.title ?? `エージェント設定の更新に失敗しました (${res.status})`,
		);
	}
	return (await res.json()) as Environment;
}

/**
 * PUT /api/environments/{id}/volumes — マウントする共有ボリュームの宣言を全て置き換える
 * (組織内・利用可能なもの)。変更は次回起動時に反映される。
 */
export async function updateEnvironmentVolumes(
	environmentId: string,
	volumeIds: string[],
): Promise<Environment> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/volumes`, {
		method: "PUT",
		body: JSON.stringify({ volumeIds }),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(
			data.error ??
				data.title ??
				`共有ボリュームのマウント設定の更新に失敗しました (${res.status})`,
		);
	}
	return (await res.json()) as Environment;
}

/** USB 機器を使っている別の環境。 */
export interface UsbDeviceUser {
	/** 自分が見られない環境なら null (name は「別のエージェント」)。 */
	environmentId: string | null;
	name: string;
}

/** デバイスのランナーが報告した USB 機器 (選んでいるが報告に無いものは connected が false)。 */
export interface UsbDevice {
	/** 機器を見分けるキー (id:<vid>:<pid>:<serial> か port:<差した口>)。 */
	key: string;
	vendorId: string | null;
	productId: string | null;
	manufacturer: string | null;
	product: string | null;
	serial: string | null;
	/** 差した口 (例 1-1.3)。 */
	port: string | null;
	/** 機器の下の /dev のノード (例 /dev/ttyACM0)。 */
	nodes: string[];
	connected: boolean;
	/** 同じデバイスで稼働中か起動の予約中の別の環境が使っていれば、その環境。 */
	usedBy: UsbDeviceUser | null;
	/** 稼働中の環境に起動時に渡した機器の、その後の抜き差しの状態。稼働中でないか報告が無ければ null。 */
	state: "connected" | "removed" | "replugged" | null;
}

/** GET /api/environments/{id}/usb-devices の応答。 */
export interface EnvironmentUsbDevices {
	/** デバイスがオンラインか。false なら devices は最後に報告された一覧。 */
	runnerOnline: boolean;
	/** 選んでいる機器のキー (次の起動から渡す)。 */
	selected: string[];
	devices: UsbDevice[];
}

/** 1 つの環境に選べる USB 機器の上限 (backend の検査と同じ)。 */
export const MAX_ENVIRONMENT_USB_DEVICES = 8;

/** GET /api/environments/{id}/usb-devices — デバイスが報告した USB 機器と、選んでいる機器を取得する。 */
export async function getEnvironmentUsbDevices(
	environmentId: string,
	signal?: AbortSignal,
): Promise<EnvironmentUsbDevices> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/usb-devices`, {
		signal,
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `USB 機器の取得に失敗しました (${res.status})`);
	}
	return (await res.json()) as EnvironmentUsbDevices;
}

/** PUT /api/environments/{id}/usb-devices — 選ぶ USB 機器を全て置き換える。次の起動から反映される。 */
export async function updateEnvironmentUsbDevices(
	environmentId: string,
	keys: string[],
): Promise<void> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/usb-devices`, {
		method: "PUT",
		body: JSON.stringify({ keys }),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `USB 機器の設定の保存に失敗しました (${res.status})`);
	}
}

/** デバイスのランナーが報告したロボット。 */
export interface DeviceRobot {
	/** ロボットの種類 (例 agibot-g2)。 */
	kind: string;
}

/** ロボットを使っている別の環境。 */
export interface RobotUser {
	/** 自分が見られない環境なら null (name は「別のエージェント」)。 */
	environmentId: string | null;
	name: string;
}

/** GET /api/environments/{id}/robot の応答。 */
export interface EnvironmentRobot {
	runnerOnline: boolean;
	/** 次の起動からロボットを使うか。 */
	useRobot: boolean;
	/** デバイスが最後に報告したロボット。報告が無ければ null。 */
	robot: DeviceRobot | null;
	/** ランナーの版がロボットに対応していない。 */
	runnerOutdated: boolean;
	/** 同じデバイスで稼働中か起動の予約中の別の環境がロボットを使っていれば、その環境。 */
	usedBy: RobotUser | null;
}

/** GET /api/environments/{id}/robot — デバイスのロボットと、この環境がロボットを使うかを取得する。 */
export async function getEnvironmentRobot(
	environmentId: string,
	signal?: AbortSignal,
): Promise<EnvironmentRobot> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/robot`, {
		signal,
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `ロボットの設定の取得に失敗しました (${res.status})`);
	}
	return (await res.json()) as EnvironmentRobot;
}

/** PUT /api/environments/{id}/robot — ロボットを使うかを保存する。次の起動から反映される。 */
export async function updateEnvironmentRobot(
	environmentId: string,
	useRobot: boolean,
): Promise<void> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/robot`, {
		method: "PUT",
		body: JSON.stringify({ useRobot }),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `ロボットの設定の保存に失敗しました (${res.status})`);
	}
}

/**
 * GET /api/environments/{environmentId}/files/children — 指定ディレクトリの直下エントリを取得する。
 * `parent` はワークスペースルートからの相対パス (ルートは空文字列)。1 回で返る件数には
 * サーバー側の上限があり、続きがある場合は `hasMore` が true になる。
 */
export async function listEnvironmentDirectoryChildren(
	environmentId: string,
	parent: string,
	offset = 0,
): Promise<EnvironmentFileChildrenDto> {
	const params = new URLSearchParams({ parent });
	if (offset > 0) params.set("offset", String(offset));
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/children?${params}`,
	);
	if (!res.ok) throw new Error(`ワークスペースファイルの取得に失敗しました (${res.status})`);
	return (await res.json()) as EnvironmentFileChildrenDto;
}

/**
 * GET /api/environments/{environmentId}/files/search — パスの部分一致でファイルを絞り込む。
 * 採点・並べ替えは呼び出し側 (`buildQuickOpenItems`) が返ってきた部分集合に対して行う。
 */
export async function searchEnvironmentFiles(
	environmentId: string,
	query: string,
	limit?: number,
): Promise<EnvironmentFileDto[]> {
	const params = new URLSearchParams({ query });
	if (limit !== undefined) params.set("limit", String(limit));
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/search?${params}`,
	);
	if (!res.ok) throw new Error(`ファイルの検索に失敗しました (${res.status})`);
	return (await res.json()) as EnvironmentFileDto[];
}

/**
 * GET /api/environments/{environmentId}/files/lookup — 相対パスからファイル 1 件を解決する。
 * 該当ファイルが無い (ディレクトリを指す場合も含む) ときは null を返す。
 */
export async function lookupEnvironmentFileByPath(
	environmentId: string,
	path: string,
): Promise<EnvironmentFileDto | null> {
	const params = new URLSearchParams({ path });
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/lookup?${params}`,
	);
	if (res.status === 404) return null;
	if (!res.ok) throw new Error(`ファイルの解決に失敗しました (${res.status})`);
	return (await res.json()) as EnvironmentFileDto;
}

/**
 * POST /api/environments/{environmentId}/files/lookup-ids — ファイル ID をまとめて索引と照合する。
 * 索引から消えた ID は結果に含まれない。
 */
export async function lookupEnvironmentFilesByIds(
	environmentId: string,
	ids: readonly string[],
): Promise<EnvironmentFileDto[]> {
	if (ids.length === 0) return [];
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/lookup-ids`,
		{ method: "POST", body: JSON.stringify({ ids }) },
	);
	if (!res.ok) throw new Error(`ファイルの照合に失敗しました (${res.status})`);
	return (await res.json()) as EnvironmentFileDto[];
}

/**
 * GET /api/environments/{environmentId}/files/filtered — 拡張子 (後方一致) または
 * ファイル名 (完全一致) で絞り込んだファイルを全件取得する。excludedDirectories に挙げた名前の
 * ディレクトリ配下は除く。
 */
export async function listFilteredEnvironmentFiles(
	environmentId: string,
	filter: {
		suffixes?: readonly string[];
		names?: readonly string[];
		excludedDirectories?: readonly string[];
	},
): Promise<EnvironmentFileDto[]> {
	const params = new URLSearchParams();
	if (filter.suffixes?.length) params.set("suffixes", filter.suffixes.join(","));
	if (filter.names?.length) params.set("names", filter.names.join(","));
	if (filter.excludedDirectories?.length) {
		params.set("excludedDirectories", filter.excludedDirectories.join(","));
	}
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/filtered?${params}`,
	);
	if (!res.ok) throw new Error(`ワークスペースファイルの取得に失敗しました (${res.status})`);
	return (await res.json()) as EnvironmentFileDto[];
}

/** 初期ファイル索引 API の失敗。status を用いて停止競合を識別できる。 */
export class EnvironmentFilesInitializeError extends Error {
	readonly status: number;

	constructor(status: number, responseText: string) {
		super(`エージェントのファイルの初期化に失敗しました: ${status}: ${responseText}`);
		this.name = "EnvironmentFilesInitializeError";
		this.status = status;
	}
}

/** POST /api/environments/{environmentId}/files/initialize — running 環境の初期ファイル索引を作成する。 */
export async function initializeEnvironmentFiles(environmentId: string): Promise<void> {
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/initialize`,
		{ method: "POST" },
	);
	if (!res.ok) {
		throw new EnvironmentFilesInitializeError(res.status, await res.text());
	}
}

/**
 * ファイル本体をバックエンドから直接ストリーミングするエンドポイントの絶対 URL を返す
 * (ネットワーク呼び出しは行わない)。プレビュー(インライン再生・Range シーク)に使う。
 * `download` を指定すると添付ダウンロード (Content-Disposition: attachment) になる。
 * `version` を付けるとファイル更新時にブラウザキャッシュを確実に無効化できる。
 */
export function environmentFileContentUrl(
	environmentId: string,
	fileId: string,
	options?: { download?: boolean; version?: string | number },
): string {
	const params = new URLSearchParams();
	if (options?.download) params.set("download", "true");
	if (options?.version != null) params.set("v", String(options.version));
	const query = params.toString();
	return `${API_URL}/api/environments/${encodeURIComponent(environmentId)}/files/${encodeURIComponent(
		fileId,
	)}/content${query ? `?${query}` : ""}`;
}

/**
 * POST /api/environments/{environmentId}/files/html-preview-session — HTML プレビュー用の Cookie を受け取る。
 * Cookie は HttpOnly でプレビューのパスにだけ送られるため、HTML 内のスクリプトからは読めない。
 */
export async function startHtmlPreviewSession(environmentId: string): Promise<void> {
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/html-preview-session`,
		{ method: "POST" },
	);
	if (!res.ok) throw new Error(`HTML プレビューの準備に失敗しました (${res.status})`);
}

/**
 * HTML プレビューをファイルのパスで配信する URL。HTML 内の相対パスがこの URL を基準に解決され、
 * 同じディレクトリのファイルを取得できる。
 */
export function htmlPreviewUrl(
	environmentId: string,
	path: string,
	version?: string | number,
): string {
	const encodedPath = path.split("/").map(encodeURIComponent).join("/");
	const query = version == null ? "" : `?${new URLSearchParams({ v: String(version) })}`;
	return `${API_URL}/api/html-preview/${encodeURIComponent(environmentId)}/${encodedPath}${query}`;
}

/** HTML プレビュー内のリンクのクリックを横取りするスクリプトの URL。保存済みの HTML にはバックエンドが埋め込む。 */
export function htmlPreviewLinkScriptUrl(): string {
	return `${API_URL}/api/html-preview-link.js`;
}

/** サーバー側でファイルが変更されていて上書き保存が拒否されたことを表す (楽観ロックの衝突)。 */
export class EnvironmentFileConflictError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "EnvironmentFileConflictError";
	}
}

/**
 * PUT /api/environments/{environmentId}/files/{fileId}/content
 * 開いているファイルの内容を上書き保存する。`baseVersion` が編集開始時点で読み込んだ
 * `EnvironmentFileDto.version` と食い違う場合はサーバーが 409 を返し、
 * `EnvironmentFileConflictError` を投げる。`force` = true でこのチェックをスキップする。
 */
export async function saveEnvironmentFileContent(
	environmentId: string,
	fileId: string,
	content: string,
	baseVersion: number,
	force = false,
): Promise<EnvironmentFileDto> {
	const res = await apiFetch(
		`/api/environments/${encodeURIComponent(environmentId)}/files/${encodeURIComponent(fileId)}/content`,
		{
			method: "PUT",
			body: JSON.stringify({ content, baseVersion, force }),
		},
	);
	if (res.status === 409) {
		throw new EnvironmentFileConflictError("ファイルがサーバー側で変更されています。");
	}
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `保存に失敗しました (${res.status})`);
	}
	return (await res.json()) as EnvironmentFileDto;
}

// --- Admin ---

export type UserRole = "Member" | "Admin" | "SuperAdmin";

export type UserPermission =
	| "manageProjects"
	| "manageAgents"
	| "manageSharedVolumes"
	| "runTraining"
	| "viewBilling";

export type UserStatus = "Active" | "Invited" | "Inactive";

export interface User {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	organizationId: string;
	organizationName: string;
	role: UserRole;
	/** メンバーに個別付与した権限。管理者以上では常に空 (全権限を暗黙に持つ)。 */
	permissions: UserPermission[];
	createdAt: string;
	status: UserStatus;
	/** 認証アプリかパスキーを 1 つ以上登録しているか。 */
	mfaEnabled: boolean;
}

export interface Organization {
	id: string;
	name: string;
	createdAt: string;
	/** この組織に超管理者を付与できるか (サーバー側の許可リストで決まる)。 */
	superAdminAllowed: boolean;
	/** 呼び出し元がこの組織にユーザーを招待できるか (自社組織は開発者のみ)。 */
	inviteAllowed: boolean;
}

export async function listUsers(): Promise<User[]> {
	const res = await apiFetch("/api/users");
	if (!res.ok) throw new Error(`ユーザーの取得に失敗しました (${res.status})`);
	return res.json();
}

export async function updateUserAccess(
	userId: string,
	role: UserRole,
	permissions: UserPermission[],
): Promise<void> {
	const res = await apiFetch(`/api/users/${encodeURIComponent(userId)}/access`, {
		method: "PUT",
		body: JSON.stringify({ role, permissions }),
	});
	if (!res.ok) throw new Error(await apiErrorMessage(res, "権限の保存に失敗しました"));
}

export async function listOrganizations(): Promise<Organization[]> {
	const res = await apiFetch("/api/organizations");
	if (!res.ok) throw new Error(`組織の取得に失敗しました (${res.status})`);
	return res.json();
}

export interface OrganizationIpAllowlist {
	cidrs: string[];
	/** 呼び出し元の現在の接続元 IP。backend が特定できなければ null。 */
	callerIp: string | null;
}

function ipAllowlistPath(organizationId: string): string {
	return `/api/organizations/${encodeURIComponent(organizationId)}/ip-allowlist`;
}

export async function getOrganizationIpAllowlist(
	organizationId: string,
): Promise<OrganizationIpAllowlist> {
	const res = await apiFetch(ipAllowlistPath(organizationId));
	if (!res.ok)
		throw new ApiStatusError(
			await apiErrorMessage(res, "接続元 IP 制限の取得に失敗しました"),
			res.status,
		);
	return res.json();
}

function passwordPolicyPath(organizationId: string): string {
	return `/api/organizations/${encodeURIComponent(organizationId)}/password-policy`;
}

export async function getOrganizationPasswordPolicy(
	organizationId: string,
): Promise<PasswordCharacterRequirements> {
	const res = await apiFetch(passwordPolicyPath(organizationId));
	if (!res.ok) throw new Error(await apiErrorMessage(res, "パスワード要件を取得できませんでした"));
	return res.json();
}

export async function updateOrganizationPasswordPolicy(
	organizationId: string,
	requirements: PasswordCharacterRequirements,
): Promise<PasswordCharacterRequirements> {
	const res = await apiFetch(passwordPolicyPath(organizationId), {
		method: "PUT",
		body: JSON.stringify(requirements),
	});
	if (!res.ok) throw new Error(await apiErrorMessage(res, "パスワード要件を保存できませんでした"));
	return res.json();
}

/** 400 の検証エラー (形式・件数・締め出し防止) は backend の文言をそのままメッセージにする。 */
export async function updateOrganizationIpAllowlist(
	organizationId: string,
	cidrs: string[],
): Promise<OrganizationIpAllowlist> {
	const res = await apiFetch(ipAllowlistPath(organizationId), {
		method: "PUT",
		body: JSON.stringify({ cidrs }),
	});
	if (!res.ok)
		throw new ApiStatusError(
			await apiErrorMessage(res, "接続元 IP 制限の保存に失敗しました"),
			res.status,
		);
	return res.json();
}

export interface CreateUserPayload {
	firstName: string;
	lastName: string;
	email: string;
	organizationId: string;
	role: UserRole;
	permissions: UserPermission[];
}

export interface CreateUserResult {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	organizationId: string;
	role: UserRole;
	inviteId: string;
	inviteUrl: string;
	inviteExpiresAt: string;
}

export interface InvitePreview {
	email: string;
	firstName: string;
	lastName: string;
	organizationName: string;
	passwordRequirements: PasswordCharacterRequirements;
}

export async function setUserActive(id: string, active: boolean): Promise<void> {
	const res = await apiFetch(`/api/users/${id}/set-active`, {
		method: "POST",
		body: JSON.stringify({ active }),
	});
	if (!res.ok) {
		const message = active
			? `ユーザーの再有効化に失敗しました (${res.status})`
			: `ユーザーの無効化に失敗しました (${res.status})`;
		throw new Error(message);
	}
}

export async function createOrganization(name: string): Promise<Organization> {
	const res = await apiFetch("/api/organizations", {
		method: "POST",
		body: JSON.stringify({ name }),
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? "この組織名はすでに使用されています。");
	}
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.title ?? `組織の作成に失敗しました (${res.status})`);
	}
	return res.json();
}

export async function emailInvite(userId: string): Promise<void> {
	const res = await apiFetch(`/api/users/${userId}/email-invite`, { method: "POST" });
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `招待メールの送信に失敗しました (${res.status})`);
	}
}

export async function reinviteUser(id: string): Promise<CreateUserResult> {
	const res = await apiFetch(`/api/users/${id}/reinvite`, { method: "POST" });
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? data.title ?? `招待リンクの発行に失敗しました (${res.status})`);
	}
	return res.json();
}

export type PasswordResetResult = "submitted" | "rateLimited";

export async function requestPasswordReset(email: string): Promise<PasswordResetResult> {
	// The backend deliberately returns the same 202 whether or not the email
	// matches an account, so we treat any 2xx as "submitted." 429 is the one
	// status that's safe to surface — it's per-email, not per-existence.
	const res = await apiFetch("/api/auth/forgot-password", {
		method: "POST",
		body: JSON.stringify({ email }),
		redirectOn401: false,
	});
	if (res.status === 429) return "rateLimited";
	return "submitted";
}

export class InviteUnavailableError extends Error {
	constructor(public readonly reason: "notFound" | "expired") {
		super(reason === "expired" ? "この招待は期限切れです。" : "この招待は無効です。");
		this.name = "InviteUnavailableError";
	}
}

export async function getInvite(inviteId: string): Promise<InvitePreview> {
	const res = await apiFetch(`/api/invites/${inviteId}`, { redirectOn401: false });
	if (res.status === 404) throw new InviteUnavailableError("notFound");
	if (res.status === 410) throw new InviteUnavailableError("expired");
	if (!res.ok) throw new Error(`招待の取得に失敗しました (${res.status})`);
	return res.json();
}

export async function acceptInvite(inviteId: string, password: string): Promise<void> {
	const res = await apiFetch(`/api/invites/${inviteId}/accept`, {
		method: "POST",
		body: JSON.stringify({ password }),
		redirectOn401: false,
	});
	if (res.status === 404) throw new InviteUnavailableError("notFound");
	if (res.status === 410) throw new InviteUnavailableError("expired");
	if (res.status === 400) {
		const data = await res.json().catch(() => ({}));
		const passwordErr = data.errors?.password?.[0];
		throw new Error(passwordErr ?? "パスワードが無効です。");
	}
	if (!res.ok) throw new Error(await apiErrorMessage(res, "パスワードの設定に失敗しました"));
}

export async function createUser(payload: CreateUserPayload): Promise<CreateUserResult> {
	const res = await apiFetch("/api/users", {
		method: "POST",
		body: JSON.stringify({
			firstName: payload.firstName,
			lastName: payload.lastName,
			email: payload.email,
			organizationId: payload.organizationId,
			role: payload.role,
			permissions: payload.permissions,
		}),
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? "このメールアドレスはすでに使用されています。");
	}
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.title ?? `ユーザーの作成に失敗しました (${res.status})`);
	}
	return res.json();
}

// --- Auth ---

export interface LoginPayload {
	email: string;
	password: string;
}

/** パスワードが正しければ、多要素認証が要るときはその内容を、要らなければ null を返す (セッション発行済み)。 */
export async function login(payload: LoginPayload): Promise<MfaChallenge | null> {
	const res = await fetch(`${API_URL}/api/auth/login`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(payload),
	});
	if (!res.ok) {
		throw new Error(loginErrorMessage(res.status));
	}
	const data = (await res.json()) as { mfa?: MfaChallenge };
	return data.mfa ?? null;
}

function loginErrorMessage(status: number): string {
	if (status === 401)
		return "ログインできませんでした。メールアドレスとパスワードを確認してください。組織で接続元を制限している場合は、許可されたネットワークから接続してください。";
	if (status === 400) return "入力内容を確認してください。";
	if (status === 429)
		return "試行回数が多すぎます。しばらくしてからもう一度お試しいただくか、「パスワードをお忘れですか？」から再設定してください。";
	if (status >= 500) return "サーバーエラーが発生しました。時間をおいて再度お試しください。";
	return `サインインに失敗しました (${status})`;
}

/** 成り代わり元 (SuperAdmin) のユーザー情報。/api/auth/me が成り代わり中のみ返す。 */
export interface Impersonator {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
}

/** POST /api/auth/impersonate — 指定ユーザーとして操作を開始する (SuperAdmin 専用)。 */
export async function impersonateUser(userId: string): Promise<void> {
	const res = await apiFetch("/api/auth/impersonate", {
		method: "POST",
		body: JSON.stringify({ userId }),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `ユーザーとしての操作開始に失敗しました (${res.status})`);
	}
}

/** POST /api/auth/impersonate/stop — 成り代わりを終了して元のユーザーに戻る。 */
export async function stopImpersonation(): Promise<void> {
	const res = await apiFetch("/api/auth/impersonate/stop", { method: "POST" });
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `操作の終了に失敗しました (${res.status})`);
	}
}

export async function logout(): Promise<void> {
	// サーバーへの logout 通知が失敗してもクライアントの遷移は止めない。
	// 認証 Cookie はサーバー側で TTL 切れにより無効化される。
	await apiFetch(`/api/auth/logout`, { method: "POST" }).catch((err: unknown) =>
		console.warn("logout request failed", err),
	);
	window.location.replace("/login");
}

// --- Chat sessions & messages ---
//
// メッセージ本体は chat_messages テーブルに保存される。メタデータ系の関数は
// タイトル等のみを扱う。

/** チャット画面で選択できるモデル。プロバイダー固有のモデル ID は含めない。 */
export interface ChatModelOption {
	id: string;
	name: string;
	supportedEfforts: ChatEffort[];
	/** バックエンドが設定した既定モデルか。旧 API では未指定。 */
	isDefault?: boolean;
	/** このモデルを既定として選ぶときの推論量。旧 API では未指定。 */
	defaultEffort?: ChatEffort | null;
	/** メニュー直下ではなく「そのほか」サブメニューに並べるか。旧 API では未指定。 */
	listedUnderOthers?: boolean;
}

/** GET /api/models — 現在のユーザーが利用できるチャットモデルを取得する。 */
export async function listChatModels(): Promise<ChatModelOption[]> {
	const res = await apiFetch("/api/models");
	if (!res.ok) throw new Error(`モデル一覧の取得に失敗しました (${res.status})`);
	return (await res.json()) as ChatModelOption[];
}

/**
 * 新規チャットが属する既存環境の指定。チャットは既存環境の上でのみ開始できる。
 */
export type NewChatEnvironment = { environmentId: string; permissionMode?: PermissionMode };

/**
 * Create a blank chat session row on the backend.
 * Called when the user sends the first message of a new-chat draft.
 * The environment selection determines the chat's environment_id.
 */
export async function createBlankChat(environment: NewChatEnvironment): Promise<ChatMetadata> {
	const res = await apiFetch(`/api/chats`, {
		method: "POST",
		body: JSON.stringify({
			...environment,
			permissionMode: environment.permissionMode ?? readNewChatPermissionMode(),
		}),
	});
	if (!res.ok) throw new Error(`Failed to create chat: ${res.status}`);
	return res.json();
}

/**
 * POST /api/chats/{chatId}/branch — 指定ユーザーメッセージの直前までの履歴を複製した
 * 新しいチャットを作る。分岐元は変化しない。
 */
export async function branchChat(chatId: string, messageId: string): Promise<ChatMetadata> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/branch`, {
		method: "POST",
		body: JSON.stringify({ messageId }),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `チャットの分岐に失敗しました (${res.status})`);
	}
	return res.json();
}

/** GET /api/chats/{chatId}/image-placeholders — モデル入力から外している画像の位置一覧。 */
export async function listChatImagePlaceholders(chatId: string): Promise<ImagePlaceholderTarget[]> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/image-placeholders`);
	if (!res.ok) throw new Error(`画像の置き換え状態の取得に失敗しました (${res.status})`);
	return res.json();
}

/**
 * POST /api/chats/{chatId}/image-placeholders — 指定位置の画像をモデル入力から外す。履歴は書き換えず、
 * 以降のモデル呼び出しでプレースホルダー文に差し替わる。更新後の一覧を返す。
 */
export async function addChatImagePlaceholders(
	chatId: string,
	targets: readonly ImagePlaceholderTarget[],
): Promise<ImagePlaceholderTarget[]> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/image-placeholders`, {
		method: "POST",
		body: JSON.stringify({ targets }),
	});
	if (!res.ok) {
		throw new Error(
			(await readApiError(res, `画像の置き換えに失敗しました (${res.status})`)).message,
		);
	}
	return res.json();
}

/** DELETE /api/chats/{chatId}/image-placeholders — 置き換えを取り消し、画像を再びモデル入力へ載せる。更新後の一覧を返す。 */
export async function removeChatImagePlaceholders(
	chatId: string,
	targets: readonly ImagePlaceholderTarget[],
): Promise<ImagePlaceholderTarget[]> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/image-placeholders`, {
		method: "DELETE",
		body: JSON.stringify({ targets }),
	});
	if (!res.ok) {
		throw new Error(
			(await readApiError(res, `置き換えの取り消しに失敗しました (${res.status})`)).message,
		);
	}
	return res.json();
}

export interface ToolPolicyRule {
	action: "deny" | "ask" | "allow";
	toolName: string | null;
	argPattern: string | null;
}

/** 対象ファイルポリシーのアクセスレベル。制限の強い順に並べる。 */
export type FileAccessLevel = "denied" | "readOnly" | "confirmEdit" | "editable";

export interface FilePolicyRule {
	access: FileAccessLevel;
	pattern: string;
}

export class ToolPolicyConflictError extends Error {
	constructor(
		message: string,
		readonly revision: number | null = null,
	) {
		super(message);
		this.name = "ToolPolicyConflictError";
	}
}

async function readApiError(
	res: Response,
	fallback: string,
): Promise<{ message: string; revision: number | null }> {
	const body = res.headers.get("content-type")?.includes("json")
		? ((await res.json()) as { error?: string; revision?: number })
		: {};
	return { message: body.error ?? fallback, revision: body.revision ?? null };
}

export interface ToolPolicyDocument {
	tools: ToolPolicyRule[];
	files: FilePolicyRule[];
	/** 楽観的排他用。GET が返し、PUT が expectedRevision として送る。 */
	revision: number;
}

export async function getToolPolicyDocument(projectId: string): Promise<ToolPolicyDocument> {
	const res = await apiFetch(
		`/api/projects/${encodeURIComponent(projectId)}/tool-policy/rules-document`,
	);
	if (!res.ok) throw new Error("ツール実行ポリシーを取得できませんでした。");
	return (await res.json()) as ToolPolicyDocument;
}

/**
 * ツール実行ルールと対象ファイルのルールを一括置換する。
 * 成功時は新しい revision を返す (連続保存のために保持側で更新する)。
 * 競合 (409) は ToolPolicyConflictError を投げる。
 */
export async function replaceToolPolicyDocument(
	projectId: string,
	input: ToolPolicyDocument,
): Promise<number> {
	const res = await apiFetch(
		`/api/projects/${encodeURIComponent(projectId)}/tool-policy/rules-document`,
		{
			method: "PUT",
			body: JSON.stringify({
				tools: input.tools,
				files: input.files,
				expectedRevision: input.revision,
			}),
		},
	);
	if (!res.ok) {
		const error = await readApiError(res, "ポリシーを保存できませんでした。");
		if (res.status === 409) throw new ToolPolicyConflictError(error.message, error.revision);
		throw new Error(error.message);
	}
	const body = (await res.json()) as { revision: number };
	return body.revision;
}

export async function updateChatPermissionMode(
	chatId: string,
	permissionMode: PermissionMode,
	expectedRevision: number,
): Promise<ChatMetadata> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/permission-mode`, {
		method: "POST",
		body: JSON.stringify({
			permissionMode,
			expectedRevision,
		}),
	});
	if (res.status === 409) {
		const error = await readApiError(res, "チャットが更新されています。再読み込みしてください。");
		throw new ToolPolicyConflictError(error.message, error.revision);
	}
	if (!res.ok) {
		const error = await readApiError(res, "確認モードを変更できませんでした。");
		throw new Error(error.message);
	}
	return (await res.json()) as ChatMetadata;
}

export type ToolApprovalDecision = "approve" | "deny";

export async function resolveToolApproval(
	chatId: string,
	toolUseId: string,
	decision: ToolApprovalDecision,
): Promise<void> {
	const res = await apiFetch(
		`/api/chats/${encodeURIComponent(chatId)}/tool-approvals/${encodeURIComponent(toolUseId)}`,
		{
			method: "POST",
			body: JSON.stringify({ decision }),
		},
	);
	if (res.status === 409) {
		const error = await readApiError(res, "この承認は他の画面で更新されています。");
		throw new ToolPolicyConflictError(error.message, error.revision);
	}
	if (!res.ok) {
		const error = await readApiError(res, "承認結果を送信できませんでした。");
		throw new Error(error.message);
	}
}

/**
 * Persist chat metadata changes (title). Throws on non-OK responses.
 */
export async function saveChat(input: {
	chatId: string;
	title?: string | null;
}): Promise<ChatMetadata> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(input.chatId)}`, {
		method: "POST",
		body: JSON.stringify({
			title: input.title ?? null,
		}),
	});
	if (!res.ok) throw new Error(`チャットの保存に失敗しました (${res.status})`);
	return (await res.json()) as ChatMetadata;
}

/**
 * POST /api/chats/{chatId}/title — タイトルがまだないチャットに限り、サーバ側で会話内容から
 * タイトルを生成・永続化し、更新後のメタデータを返す。既にタイトルがある (改名済み) ときは
 * 生成せずそのタイトルを、生成に失敗したときは title が null のまま返すため、
 * 呼び出し側は title が得られたときのみ表示を更新する。
 */
export async function generateChatTitle(chatId: string): Promise<ChatMetadata> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/title`, { method: "POST" });
	if (!res.ok) {
		throw new ApiStatusError(
			await apiErrorMessage(res, "チャットタイトルの生成に失敗しました"),
			res.status,
		);
	}
	return (await res.json()) as ChatMetadata;
}

/** Load a chat session's metadata by DB id. Returns null if the chat is not found. */
export async function loadChat(chatId: string): Promise<ChatMetadata | null> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}`);
	if (res.status === 404) return null;
	if (!res.ok) throw new Error(`Failed to load chat: ${res.status}`);
	return res.json();
}

/** List saved chat sessions. */
export async function listChats(): Promise<ChatListEntry[]> {
	const res = await apiFetch(`/api/chats`);
	if (!res.ok) return [];
	return res.json();
}

/**
 * GET /api/chats/activity — 可視チャットの状態と最終活動時刻を返す (左ペインのインジケーター用)。
 * 定期ポーリングから呼ぶため 401 でのログイン画面遷移は行わない。背景の取得が画面を差し替えると、
 * 書きかけのメッセージ (メモリ上のみ) が予告なく消える。遷移は次のユーザー操作に任せる。
 */
export async function listChatActivity(): Promise<ChatActivityEntry[]> {
	const res = await apiFetch(`/api/chats/activity`, { redirectOn401: false });
	if (!res.ok) throw new Error(`チャットの実行状態の取得に失敗しました (${res.status})`);
	return (await res.json()) as ChatActivityEntry[];
}

/** POST /api/chats/{chatId}/archive — チャットをアーカイブする。 */
export async function archiveChat(chatId: string): Promise<void> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/archive`, {
		method: "POST",
	});
	if (!res.ok) throw new Error(`チャットのアーカイブに失敗しました (${res.status})`);
}

/** POST /api/chats/{chatId}/unarchive — チャットのアーカイブを解除する。 */
export async function unarchiveChat(chatId: string): Promise<void> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/unarchive`, {
		method: "POST",
	});
	if (!res.ok) throw new Error(`チャットのアーカイブ解除に失敗しました (${res.status})`);
}

/**
 * DELETE /api/chats/{chatId} — チャットを完全に削除する (冪等)。
 * メッセージ・ジョブ・モニター・学習記録もすべて削除され、元に戻せない。
 * 実行中の作業がある場合は 409 が返り、その理由をエラーメッセージとして投げる。
 */
export async function deleteChat(chatId: string): Promise<void> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}`, {
		method: "DELETE",
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? "実行中の作業があるため削除できません。");
	}
	// 既に消えているチャットへの再削除 (別タブ・別ユーザーが先に削除した等) は 404 になるが、
	// 望んだ最終状態は成立しているので成功として扱う。エラーにすると呼び出し側がローカル一覧の
	// 除去まで到達できず、幽霊行がリロードまで残る。
	if (res.status === 404) return;
	if (!res.ok) throw new Error(`チャットの削除に失敗しました (${res.status})`);
}

/** GET /api/chats/{chatId}/messages — order_index 昇順。before より前で、limit があれば末尾からその件数だけ返る。 */
export async function listChatMessages(
	chatId: string,
	page: { before?: number; limit?: number } = {},
): Promise<ChatMessageDto[]> {
	const params = new URLSearchParams();
	if (page.before !== undefined) params.set("before", String(page.before));
	if (page.limit !== undefined) params.set("limit", String(page.limit));
	const query = params.size > 0 ? `?${params}` : "";
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/messages${query}`);
	if (res.status === 404) return [];
	if (!res.ok) throw new Error(`Failed to load messages: ${res.status}`);
	return (await res.json()) as ChatMessageDto[];
}

/** GET /api/chats/{chatId}/messages/queue — Seq 昇順の送信待ちメッセージ。チャットが無ければ空配列。 */
export async function listQueuedChatMessages(chatId: string): Promise<QueuedChatMessageDto[]> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/messages/queue`);
	if (res.status === 404) return [];
	if (!res.ok) throw new Error(`送信待ちメッセージの取得に失敗しました (${res.status})`);
	return (await res.json()) as QueuedChatMessageDto[];
}

/**
 * 送信待ちメッセージが既にジョブへ取り込まれた (昇格済み) か、そもそも存在しないことを表すエラー (HTTP 404)。
 * 呼び出し側はこれを捕捉して queue / messages を再取得し、二重表示を解消する。
 */
export class QueuedMessageAlreadyPromotedError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "QueuedMessageAlreadyPromotedError";
	}
}

/**
 * DELETE /api/chats/{chatId}/messages/queue/{messageId} — 送信待ちメッセージを取り消す。
 * 既に昇格済み / 存在しない場合 (404) は `QueuedMessageAlreadyPromotedError` を投げる。
 */
export async function deleteQueuedChatMessage(chatId: string, messageId: string): Promise<void> {
	const res = await apiFetch(
		`/api/chats/${encodeURIComponent(chatId)}/messages/queue/${encodeURIComponent(messageId)}`,
		{ method: "DELETE" },
	);
	if (res.status === 404) {
		throw new QueuedMessageAlreadyPromotedError("送信待ちメッセージは既に取り込まれています。");
	}
	if (!res.ok) throw new Error(`送信待ちメッセージの削除に失敗しました (${res.status})`);
}

/** GET /api/chats/{chatId}/monitors — createdAt 降順で返る。チャットが無ければ空配列。 */
export async function listChatMonitors(chatId: string): Promise<ChatMonitor[]> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/monitors`);
	if (res.status === 404) return [];
	if (!res.ok) throw new Error(`Failed to load monitors: ${res.status}`);
	return (await res.json()) as ChatMonitor[];
}

/** GET /api/chats/{chatId}/subagent-runs — チャットに属するサブエージェント実行を返す。 */
export async function listChatSubagentRuns(chatId: string): Promise<ChatSubagentRun[]> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/subagent-runs`);
	if (res.status === 404) return [];
	if (!res.ok) throw new Error(`サブエージェント実行の取得に失敗しました (${res.status})`);
	return (await res.json()) as ChatSubagentRun[];
}

/**
 * サブエージェント実行と実行履歴を取得する。
 * `fromOrder` を渡すと messages はその order より後の差分のみになる (run は常に全体)。
 */
export async function getChatSubagentRun(
	chatId: string,
	runId: string,
	options?: { fromOrder?: number; signal?: AbortSignal },
): Promise<SubagentRunDetail> {
	const path = `/api/chats/${encodeURIComponent(chatId)}/subagent-runs/${encodeURIComponent(runId)}`;
	const query = options?.fromOrder === undefined ? "" : `?fromOrder=${options.fromOrder}`;
	const res = await apiFetch(`${path}${query}`, { signal: options?.signal });
	if (!res.ok)
		throw new ApiStatusError(
			`サブエージェント実行履歴の取得に失敗しました (${res.status})`,
			res.status,
		);
	return (await res.json()) as SubagentRunDetail;
}

export interface SubagentRunStopResult {
	id: string;
	status: SubagentRunStatus;
}

/**
 * POST /api/chats/{chatId}/subagent-runs/{runId}/stop — サブエージェント実行の停止を要求する。
 * queued は即時 cancelled、running は cancelRequested になる (200 / 202 いずれも DTO を返す)。
 * 終端済みの実行へ投げても現在の状態を返すだけで冪等。foreground 実行は対象外で 404 になる。
 */
export async function stopChatSubagentRun(
	chatId: string,
	runId: string,
): Promise<SubagentRunStopResult> {
	const res = await apiFetch(
		`/api/chats/${encodeURIComponent(chatId)}/subagent-runs/${encodeURIComponent(runId)}/stop`,
		{ method: "POST" },
	);
	if (res.status === 403) throw new Error("このサブエージェントを停止する権限がありません。");
	if (res.status === 404) throw new Error("停止対象のサブエージェント実行が見つかりません。");
	if (!res.ok) throw new Error(`サブエージェントの停止に失敗しました (${res.status})`);
	return (await res.json()) as SubagentRunStopResult;
}

/**
 * GET /api/chats/{chatId}/workflow-runs — チャットに属するワークフロー実行を返す。
 * 他の一覧と違い 404 を空配列に均さない。実行が 0 件なら 200 と空配列が返るので、404 は
 * 「チャットが無い / 見えない」か「この経路自体が無い」のどちらかで、空と同じ扱いにはできない。
 * ただし throw しても描画は変わらない点に注意 — 呼び出し側は取得できなかった一覧を書かずに
 * 前の値を残すため、初回から失敗し続けるとカードは「起動中」のまま固定され、実行中扱いが
 * 続くので 2 秒ポーリングも止まらない (復旧すればその回の取得で正しい状態に戻る)。差が出るのは
 * トーストと console.error が出ること (無言にならないこと) だけ。
 */
export async function listChatWorkflowRuns(chatId: string): Promise<ChatWorkflowRun[]> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/workflow-runs`);
	if (!res.ok) throw new Error(`ワークフロー実行の取得に失敗しました (${res.status})`);
	return (await res.json()) as ChatWorkflowRun[];
}

export interface WorkflowRunStopResult {
	id: string;
	status: WorkflowRunStatus;
}

/**
 * POST /api/chats/{chatId}/workflow-runs/{runId}/stop — ワークフロー実行の停止を要求する。
 * 配下のサブエージェント実行にも停止が伝播する。終端済みへ投げても現在の状態を返すだけで冪等。
 */
export async function stopChatWorkflowRun(
	chatId: string,
	runId: string,
): Promise<WorkflowRunStopResult> {
	const res = await apiFetch(
		`/api/chats/${encodeURIComponent(chatId)}/workflow-runs/${encodeURIComponent(runId)}/stop`,
		{ method: "POST" },
	);
	if (res.status === 403) throw new Error("このワークフローを停止する権限がありません。");
	if (res.status === 404) throw new Error("停止対象のワークフロー実行が見つかりません。");
	if (!res.ok) throw new Error(`ワークフローの停止に失敗しました (${res.status})`);
	return (await res.json()) as WorkflowRunStopResult;
}

/** チャットの追記が `expectedRevision` の不一致で拒否されたことを表すエラー (HTTP 409)。 */
export class ChatSendConflictError extends Error {
	constructor(
		message: string,
		readonly revision: number | null,
		readonly reason: string | null,
	) {
		super(message);
		this.name = "ChatSendConflictError";
	}
}

/** content block (image 等) がサーバ側の制約に違反したことを表すエラー (HTTP 400)。 */
export class ChatSendValidationError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "ChatSendValidationError";
	}
}

/**
 * POST /api/chats/{chatId}/messages — 単一のユーザー由来メッセージを会話末尾へ追記する。
 * `expectedRevision` がサーバの最新リビジョンと一致しない場合は `ChatSendConflictError` を投げる。
 * バックエンドは追記後にエージェントジョブを起票するが、その応答はレスポンスボディからは
 * 読まない。アシスタント応答・ツール呼び出し・結果はすべて SSE ストリームで受け取る。
 */
export async function postChatMessage(
	chatId: string,
	append: ChatMessageAppendDto,
): Promise<ChatSendResultDto> {
	// エージェントが日時をユーザーのタイムゾーンで伝えるために送る。
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/messages`, {
		method: "POST",
		body: JSON.stringify({ ...append, timeZone }),
	});
	if (res.status === 409) {
		const body = (await res.json().catch(() => ({}))) as {
			error?: string;
			revision?: number;
			reason?: string;
		};
		throw new ChatSendConflictError(
			body.error ?? "チャットが更新されています。再読み込みしてください。",
			body.revision ?? null,
			body.reason ?? null,
		);
	}
	if (res.status === 400) {
		const body = (await res.json().catch(() => ({}))) as { error?: string };
		throw new ChatSendValidationError(body.error ?? "メッセージの送信に失敗しました。");
	}
	if (!res.ok) throw new Error(`Failed to send message: ${res.status}`);
	return (await res.json()) as ChatSendResultDto;
}

/** 進行中のエージェントジョブ。Stop API / active job API の DTO。 */
export interface AgentJobRef {
	id: string;
	status: string;
}

export interface AgentJobStopResult {
	id: string;
	status: string;
	retractedMessage?: RetractedMessage | null;
}

export interface AgentJobRetryRequest {
	model?: string;
	effort?: ChatEffort;
	subagentModels?: Record<string, string>;
}

export interface AgentJobRetryResult {
	jobId: string;
}

export class AgentJobRetryError extends Error {
	constructor(
		message: string,
		readonly status: number,
		readonly reason: string | null,
	) {
		super(message);
		this.name = "AgentJobRetryError";
	}
}

/**
 * POST /api/chats/{chatId}/agent-jobs/{jobId}/stop — エージェントジョブの停止を要求する。
 * Pending は即時 cancelled、Running は cancelRequested になる (200 / 202 いずれも DTO を返す)。
 * 403 / 404 は例外を投げる。
 */
export async function stopAgentJob(chatId: string, jobId: string): Promise<AgentJobStopResult> {
	const res = await apiFetch(
		`/api/chats/${encodeURIComponent(chatId)}/agent-jobs/${encodeURIComponent(jobId)}/stop`,
		{ method: "POST" },
	);
	if (res.status === 403) throw new Error("このジョブを停止する権限がありません。");
	if (res.status === 404) throw new Error("停止対象のジョブが見つかりません。");
	if (!res.ok) throw new Error(`ジョブの停止に失敗しました (${res.status})`);
	return (await res.json()) as AgentJobStopResult;
}

/** POST /api/chats/{chatId}/agent-jobs/retry — 最新の失敗ターンを選択中の実行設定で再開する。 */
export async function postAgentJobRetry(
	chatId: string,
	request: AgentJobRetryRequest,
): Promise<AgentJobRetryResult> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/agent-jobs/retry`, {
		method: "POST",
		body: JSON.stringify(request),
	});
	if (!res.ok) {
		const body = (await res.json().catch(() => ({}))) as { error?: string; reason?: string };
		throw new AgentJobRetryError(
			body.error ?? `処理の再試行に失敗しました (${res.status})`,
			res.status,
			body.reason ?? null,
		);
	}
	return (await res.json()) as AgentJobRetryResult;
}

/**
 * GET /api/chats/{chatId}/agent-jobs/active — 進行中のエージェントジョブを取得する。
 * 進行中のジョブが無ければ (204) null を返す。
 */
export async function getActiveAgentJob(chatId: string): Promise<AgentJobRef | null> {
	const res = await apiFetch(`/api/chats/${encodeURIComponent(chatId)}/agent-jobs/active`);
	if (res.status === 204) return null;
	if (!res.ok) throw new Error(`進行中ジョブの取得に失敗しました (${res.status})`);
	return (await res.json()) as AgentJobRef;
}

/** バックエンド BillingSummaryDto に対応。組織のクレジット消費の集計。 */
export interface BillingSummary {
	totalCredits: number;
	byProject: BillingProjectCredits[];
}

/** プロジェクトに紐付かない消費の出どころ。バックエンド BillingUsageOrigin に対応。 */
export type BillingUsageOrigin = "helpAgent" | "deviceLicense";

/**
 * プロジェクト別のクレジット消費。組織直下 (プロジェクトに紐付かない) の消費は
 * projectId が null で、出どころ (origin) ごとに 1 行。出どころを記録する前の消費は origin も null。
 * projectId があるのに projectName が null の場合、プロジェクトは
 * 物理削除済み。projectArchivedAt はアーカイブ日時で、有効なプロジェクトは null。
 */
export interface BillingProjectCredits {
	projectId: string | null;
	origin: BillingUsageOrigin | null;
	projectName: string | null;
	projectArchivedAt: string | null;
	credits: number;
	/** イベント種別 × 環境ごとの内訳。消費クレジットの降順。 */
	byEvent: BillingEventCredits[];
}

/**
 * イベント種別 (ドット区切り識別子) × 環境 1 件分のクレジット消費。
 * 環境に紐付かないイベントは environmentId / environmentName とも null。
 * environmentId があるのに environmentName が null の場合、環境は削除済み。
 */
export interface BillingEventCredits {
	eventType: string;
	environmentId: string | null;
	environmentName: string | null;
	credits: number;
}

/**
 * GET /api/billing/summary — 組織のクレジット消費集計を取得する。
 * from (ISO 8601) 以降・現在までを対象とし、省略時は全期間。
 * eventTypes はドット区切り識別子の配列で、省略・空なら全種別。
 * organizationId は超管理者のみ自組織以外を指定でき、省略時は自組織。
 */
export async function getBillingSummary(params: {
	from?: string;
	eventTypes?: string[];
	organizationId?: string;
}): Promise<BillingSummary> {
	const search = new URLSearchParams();
	if (params.from) search.set("from", params.from);
	if (params.eventTypes && params.eventTypes.length > 0)
		search.set("eventTypes", params.eventTypes.join(","));
	if (params.organizationId) search.set("organizationId", params.organizationId);
	const qs = search.toString();
	const res = await apiFetch(`/api/billing/summary${qs ? `?${qs}` : ""}`);
	if (!res.ok) throw new Error(`課金情報の取得に失敗しました (${res.status})`);
	return (await res.json()) as BillingSummary;
}

/**
 * バックエンド BillingDailyCreditsDto に対応。JST の暦日 (yyyy-MM-dd) × イベント種別
 * ごとのクレジット消費。消費のない日 × 種別の行は含まれない。
 */
export interface BillingDailyCredits {
	date: string;
	eventType: string;
	credits: number;
}

/**
 * GET /api/billing/daily — 使用量グラフ用の日次集計を取得する。絞り込みの引数は
 * getBillingSummary と同じで、同じ母集団が日付昇順で返る。
 */
export async function getBillingDaily(params: {
	from?: string;
	eventTypes?: string[];
	organizationId?: string;
}): Promise<BillingDailyCredits[]> {
	const search = new URLSearchParams();
	if (params.from) search.set("from", params.from);
	if (params.eventTypes && params.eventTypes.length > 0)
		search.set("eventTypes", params.eventTypes.join(","));
	if (params.organizationId) search.set("organizationId", params.organizationId);
	const qs = search.toString();
	const res = await apiFetch(`/api/billing/daily${qs ? `?${qs}` : ""}`);
	if (!res.ok) throw new Error(`日次の課金集計の取得に失敗しました (${res.status})`);
	return (await res.json()) as BillingDailyCredits[];
}

/**
 * バックエンド BillingRateDto に対応。現在適用中のメータ 1 単位あたりの単価。
 * eventType はドット区切り識別子。variantKey は種別内で単価が分かれる軸の識別子
 * (モデル ID など) で、行の同定とグループ化に使う (表示はしない)。variantLabel はその軸の
 * 表示名で、種別内が一律なら両方 null。listedUnderOthers はモデル選択で「そのほか」に並ぶモデルの単価か。
 */
export interface BillingRate {
	eventType: string;
	variantKey: string | null;
	variantLabel: string | null;
	unitPriceCredits: number;
	listedUnderOthers: boolean;
}

/** バックエンド DocsPageDto に対応。利用ガイドの 1 ページ。markdown の書き方は components/docs/remark-doc-syntax.ts を参照。 */
export interface DocsPageContent {
	slug: string;
	label: string;
	markdown: string;
}

/** GET /api/docs — 利用ガイドの全ページを表示順に取得する。 */
export async function getDocsPages(signal?: AbortSignal): Promise<DocsPageContent[]> {
	const res = await apiFetch("/api/docs", { signal });
	if (!res.ok) throw new Error(`利用ガイドを取得できませんでした (${res.status})`);
	return ((await res.json()) as { pages: DocsPageContent[] }).pages;
}

/** GET /api/billing/rates — 現在適用中の単価表を取得する (過去の版は含まない)。 */
export async function getBillingRates(): Promise<BillingRate[]> {
	const res = await apiFetch("/api/billing/rates");
	if (!res.ok) throw new Error(`単価表の取得に失敗しました (${res.status})`);
	return (await res.json()) as BillingRate[];
}

/**
 * バックエンド FinanceRateDto に対応。顧客向けの単価 (BillingRate) と同じ行に、同じレートカード行の
 * 1 単位あたりの原価 (クレジット換算) を添えた社内向けの単価。原価が未設定なら unitCostCredits は null。
 */
export interface FinanceRate extends BillingRate {
	unitCostCredits: number | null;
}

/** GET /api/finance/rates — 現在適用中の単価と原価を取得する (SuperAdmin のみ)。 */
export async function getFinanceRates(): Promise<FinanceRate[]> {
	const res = await apiFetch("/api/finance/rates");
	if (!res.ok) throw new Error(`単価表の取得に失敗しました (${res.status})`);
	return (await res.json()) as FinanceRate[];
}

/** 組織の現在の残高 (全期間) と、期間内の付与・消費・失効。 */
export interface FinanceOrganizationCredits {
	organizationId: string;
	organizationName: string;
	balanceCredits: number;
	grantedCredits: number;
	consumedCredits: number;
	expiredCredits: number;
}

/** GET /api/finance/credits — 全組織のクレジットの一覧を取得する (SuperAdmin のみ)。 */
export async function getFinanceOrganizationCredits(params: {
	from?: string;
	to?: string;
}): Promise<FinanceOrganizationCredits[]> {
	const search = new URLSearchParams();
	if (params.from) search.set("from", params.from);
	if (params.to) search.set("to", params.to);
	const qs = search.toString();
	const res = await apiFetch(`/api/finance/credits${qs ? `?${qs}` : ""}`);
	if (!res.ok) throw new Error(`組織別のクレジットの取得に失敗しました (${res.status})`);
	return (await res.json()) as FinanceOrganizationCredits[];
}

/** 台帳の行の区切り (JST の暦日 / 暦月)。 */
export type FinanceLedgerInterval = "daily" | "monthly";

/**
 * 台帳の 1 行。date は JST の暦日 (yyyy-MM-dd) か暦月 (yyyy-MM)、balanceCredits はその行の
 * 終わり時点の残高。consumedByCategory は消費のあった区分の ID ごとの消費。
 */
export interface FinanceCreditLedgerRow {
	date: string;
	grantedCredits: number;
	consumedCredits: number;
	expiredCredits: number;
	balanceCredits: number;
	consumedByCategory: Record<string, number>;
}

export interface FinanceCreditLedger {
	openingBalanceCredits: number;
	/** 消費の内訳の列 (並び順どおり)。CSV の列と同じ。 */
	categories: { id: string; label: string }[];
	/** 付与・消費・失効のいずれかがあった日 (月) だけが昇順で返る。 */
	rows: FinanceCreditLedgerRow[];
}

function financeSearch(params: {
	from?: string;
	to?: string;
	interval?: FinanceLedgerInterval;
	organizationId?: string;
}): string {
	const search = new URLSearchParams();
	if (params.interval) search.set("interval", params.interval);
	if (params.from) search.set("from", params.from);
	if (params.to) search.set("to", params.to);
	if (params.organizationId) search.set("organizationId", params.organizationId);
	return search.toString();
}

/** GET /api/finance/credits/{organizationId}/ledger — 組織の台帳を取得する (SuperAdmin のみ)。 */
export async function getFinanceCreditLedger(
	organizationId: string,
	params: { from?: string; to?: string; interval: FinanceLedgerInterval },
): Promise<FinanceCreditLedger> {
	const res = await apiFetch(
		`/api/finance/credits/${encodeURIComponent(organizationId)}/ledger?${financeSearch(params)}`,
	);
	if (!res.ok) throw new Error(`クレジットの台帳の取得に失敗しました (${res.status})`);
	return (await res.json()) as FinanceCreditLedger;
}

/**
 * GET /api/finance/credits/ledger.csv — 台帳を CSV で取得する (SuperAdmin のみ)。
 * organizationId を省略すると全組織。
 */
export async function downloadFinanceCreditsCsv(params: {
	from?: string;
	to?: string;
	interval: FinanceLedgerInterval;
	organizationId?: string;
}): Promise<Blob> {
	const res = await apiFetch(`/api/finance/credits/ledger.csv?${financeSearch(params)}`);
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `クレジットの台帳の書き出しに失敗しました (${res.status})`);
	}
	return res.blob();
}

/**
 * バックエンド FinanceDeviceLicenseEventDto に対応。ライセンスの状態の変化 1 件。
 * reason・invoiceNumber は付与の行だけに入る。
 */
export interface FinanceDeviceLicenseEvent {
	occurredAt: string;
	kind: DeviceLicenseEventKind;
	licenseId: string;
	/** 今そのライセンスに結び付いて登録しているデバイスの名前。無ければ null。 */
	deviceName: string | null;
	/** 操作者の「姓 名」。操作者が記録されていなければ null。 */
	actorName: string | null;
	reason: string | null;
	invoiceNumber: string | null;
}

/** GET /api/finance/device-license-events — 組織の期間内のライセンスの変化を新しい順で取得する (SuperAdmin のみ)。 */
export async function getFinanceDeviceLicenseEvents(
	organizationId: string,
	params: { from?: string; to?: string },
): Promise<FinanceDeviceLicenseEvent[]> {
	const res = await apiFetch(
		`/api/finance/device-license-events?${financeSearch({ ...params, organizationId })}`,
	);
	if (!res.ok) throw new Error(`ライセンスの変化の取得に失敗しました (${res.status})`);
	return (await res.json()) as FinanceDeviceLicenseEvent[];
}

/**
 * GET /api/finance/device-license-events.csv — ライセンスの変化を CSV で取得する (SuperAdmin のみ)。
 * organizationId を省略すると全組織。
 */
export async function downloadFinanceDeviceLicenseEventsCsv(params: {
	from?: string;
	to?: string;
	organizationId?: string;
}): Promise<Blob> {
	const res = await apiFetch(`/api/finance/device-license-events.csv?${financeSearch(params)}`);
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `ライセンスの変化の書き出しに失敗しました (${res.status})`);
	}
	return res.blob();
}

/** バックエンド EnvPlacementNode に対応。karpenterNodePool が null のノードは常設 (マネージドノードグループ)。 */
export interface InfraNode {
	name: string;
	nodeGroup: string;
	karpenterNodePool: string | null;
	instanceType: string;
	zone: string;
	acceptsNewPods: boolean;
	createdAt: string;
	allocatableMilliCpu: number;
	allocatableMemoryBytes: number;
	/** DaemonSet の見積もりを含む request の合計。 */
	requestedMilliCpu: number;
	requestedMemoryBytes: number;
	/** AMI の版上げなどで入れ替え待ちか。入れ替え待ちの一覧を読めなければ null。 */
	isOutdated: boolean | null;
	/** 入れ替え待ちのノードから環境を移し終える期限 (ISO 8601)。無ければ null。 */
	replacementDeadline: string | null;
}

/** バックエンド InfraPlacementEnvironment に対応。nodeName が null の環境はノードの割り当て待ち。 */
export interface InfraEnvironment {
	environmentId: string;
	environmentName: string | null;
	organizationId: string | null;
	organizationName: string | null;
	projectName: string | null;
	instanceSize: EnvironmentInstanceSize | null;
	nodeName: string | null;
	phase: string;
	ready: boolean;
	terminating: boolean;
	createdAt: string;
	/** sidecar を含む Pod の request の合計。 */
	milliCpu: number;
	memoryBytes: number;
	/** 環境コンテナの limit。 */
	limitMilliCpu: number;
	limitMemoryBytes: number;
	schedulingMessage: string | null;
	/** ノードの入れ替えで再起動される期限 (ISO 8601)。予定が無ければ null。 */
	scheduledRestartAt: string | null;
}

export interface InfraPlacement {
	observedAt: string;
	nodes: InfraNode[];
	environments: InfraEnvironment[];
}

/** GET /api/infra/placement — env ノードと環境の現在の配置を取得する (SuperAdmin のみ)。 */
export async function getInfraPlacement(): Promise<InfraPlacement> {
	const res = await apiFetch("/api/infra/placement");
	if (res.status === 404) throw new Error("環境を Kubernetes で動かしていないため表示できません。");
	if (!res.ok) throw new Error(`ノードと環境の配置の取得に失敗しました (${res.status})`);
	return (await res.json()) as InfraPlacement;
}

/** バックエンド InfraSharedVolume に対応。 */
export interface InfraSharedVolume {
	id: string;
	name: string;
	organizationId: string;
	organizationName: string;
	state: SharedVolumeState;
	measuredBytes: number | null;
	measuredAt: string | null;
	attachedEnvironmentCount: number;
	provisionError: string | null;
	createdAt: string;
}

/** バックエンド InfraVolumeSnapshot に対応。停止中の環境ボリュームの退避先。環境行が無ければ名前は null。 */
export interface InfraVolumeSnapshot {
	name: string;
	environmentId: string | null;
	environmentName: string | null;
	organizationId: string | null;
	organizationName: string | null;
	projectName: string | null;
	readyToUse: boolean;
	deleting: boolean;
	restoreSizeBytes: number | null;
	error: string | null;
	createdAt: string | null;
}

export interface InfraStorage {
	observedAt: string;
	sharedVolumes: InfraSharedVolume[];
	volumeSnapshots: InfraVolumeSnapshot[];
}

/** GET /api/infra/storage — 全組織の共有ボリュームとボリュームスナップショットを取得する (SuperAdmin のみ)。 */
export async function getInfraStorage(): Promise<InfraStorage> {
	const res = await apiFetch("/api/infra/storage");
	if (!res.ok) throw new Error(`ストレージの取得に失敗しました (${res.status})`);
	return (await res.json()) as InfraStorage;
}

/** バックエンド SandboxRunState に対応。 */
export type SandboxRunState =
	| "running"
	| "pending"
	| "waitingForCapacity"
	| "shuttingDown"
	| "errored";

/** バックエンド InfraEnvironmentRunner に対応。runnerVersion はハートビートで報告された版で、報告が無ければ null。 */
export interface InfraEnvironmentRunner {
	id: string;
	name: string;
	runnerVersion: string | null;
	online: boolean;
}

/**
 * バックエンド InfraEnvironmentSummary に対応。runner が null ならクラウドの環境。runState が null なら停止中で、
 * startedAt は今の稼働の開始時刻 (ISO 8601)。
 */
export interface InfraEnvironmentSummary {
	id: string;
	name: string;
	organizationId: string;
	organizationName: string;
	projectName: string;
	runner: InfraEnvironmentRunner | null;
	runState: SandboxRunState | null;
	startupFailed: boolean;
	startedAt: string | null;
	instanceSize: EnvironmentInstanceSize | null;
	createdAt: string;
}

export interface InfraEnvironments {
	observedAt: string;
	environments: InfraEnvironmentSummary[];
}

/** GET /api/infra/environments — 全組織の環境と稼働状態を取得する (SuperAdmin のみ)。 */
export async function getInfraEnvironments(): Promise<InfraEnvironments> {
	const res = await apiFetch("/api/infra/environments");
	if (!res.ok) throw new Error(`環境の取得に失敗しました (${res.status})`);
	return (await res.json()) as InfraEnvironments;
}

/** バックエンド InfraStartFailureCategory に対応。 */
export type InfraStartFailureCategory = "stockout" | "quota" | "otherCapacity" | "other";

/** バックエンド InfraStartFailure に対応。detail は新しい 100 件にだけ入り、それより古い行は null。 */
export interface InfraStartFailure {
	id: string;
	occurredAt: string;
	environmentId: string;
	environmentName: string;
	organizationName: string;
	kind: "failed" | "waitingForCapacity";
	reason: string | null;
	category: InfraStartFailureCategory;
	instanceSize: EnvironmentInstanceSize;
	instanceType: string | null;
	errorCodes: string[];
	errorType: string | null;
	detail: string | null;
}

/** truncated は期間内の行が上限 (5000 件) を超え、古い行を省いたこと。 */
export interface InfraStartFailures {
	from: string;
	to: string;
	truncated: boolean;
	entries: InfraStartFailure[];
}

/** GET /api/infra/start-failures — 全組織の起動失敗の台帳を新しい順に取得する (SuperAdmin のみ)。 */
export async function getInfraStartFailures(days: number): Promise<InfraStartFailures> {
	const res = await apiFetch(`/api/infra/start-failures?days=${days}`);
	if (!res.ok) throw new Error(`起動失敗の取得に失敗しました (${res.status})`);
	return (await res.json()) as InfraStartFailures;
}

/** バックエンド DbQueryStatDto に対応。maxMs 以外は集計期間内の増分。 */
export interface DbQueryStat {
	queryId: string;
	query: string;
	/** クエリのタグ (TagWith か生 SQL 先頭の `-- タグ`)。書き換えた前後のクエリ ID をまとめて推移を見るのに使う。 */
	tag: string | null;
	calls: number;
	totalMs: number;
	meanMs: number;
	stddevMs: number;
	/** pg_stat_statements は最大を期間別に持たないため、統計のリセット以降の最大。 */
	maxMs: number;
	/** 1 回あたりに返した行数の平均。 */
	meanRows: number;
	cacheHitRatio: number | null;
	/** 直前の同じ長さの期間 (previousSince から since まで) の実行回数。 */
	previousCalls: number;
	previousTotalMs: number;
}

/** バックエンド DbQueryStatsDto に対応。available が false なら pg_stat_statements が使えない。 */
export interface DbQueryStats {
	available: boolean;
	observedAt: string;
	since: string | null;
	/** 直前の同じ長さの期間の始点。遡れないときは null。 */
	previousSince: string | null;
	queries: DbQueryStat[];
}

export type DbQuerySort = "Total" | "Mean" | "Stddev" | "Max" | "Calls" | "MeanRows";

/** GET /api/infra/performance/queries — 直近 hours 時間に重かった DB クエリを取得する (SuperAdmin のみ)。 */
export async function getDbQueryStats(params: {
	hours: number;
	sort: DbQuerySort;
	limit: number;
	/** 上位に入らなくても返してほしいクエリ。 */
	pinned: string[];
	/** 上位に入らなくても返してほしいタグ。書き換えて変わった新しいクエリ ID も返す。 */
	pinnedTags: string[];
}): Promise<DbQueryStats> {
	const query = new URLSearchParams({
		hours: String(params.hours),
		sort: params.sort,
		limit: String(params.limit),
	});
	for (const id of params.pinned) query.append("pinned", id);
	for (const tag of params.pinnedTags) query.append("pinnedTag", tag);
	const res = await apiFetch(`/api/infra/performance/queries?${query}`);
	if (!res.ok) throw new Error(`DB クエリの統計の取得に失敗しました (${res.status})`);
	return (await res.json()) as DbQueryStats;
}

/** バックエンド DbQueryTrendPointDto に対応。写しが無く値が分からない区間は calls も null。 */
export interface DbQueryTrendPoint {
	start: string;
	end: string;
	calls: number | null;
	meanMs: number | null;
	/** 区間内に最も多く実行されたクエリ ID。実行されなかったか、値が分からないときは null。 */
	queryId: string | null;
}

/** バックエンド DbQueryVersionDto に対応。文面が残っていなければ query は null。 */
export interface DbQueryVersion {
	queryId: string;
	query: string | null;
}

/** バックエンド DbQueryTrendDto に対応。available が false なら pg_stat_statements が使えない。 */
export interface DbQueryTrend {
	available: boolean;
	points: DbQueryTrendPoint[];
	/** 点の queryId に現れるクエリ ID と文面。現れた順。 */
	versions: DbQueryVersion[];
}

/** GET /api/infra/performance/queries/{queryId}/trend — DB クエリの実行回数と平均時間の推移を取得する (SuperAdmin のみ)。 */
export async function getDbQueryTrend(params: {
	queryId: string;
	hours: number;
}): Promise<DbQueryTrend> {
	const res = await apiFetch(
		`/api/infra/performance/queries/${encodeURIComponent(params.queryId)}/trend?hours=${params.hours}`,
	);
	if (!res.ok) throw new Error(`DB クエリの推移の取得に失敗しました (${res.status})`);
	return (await res.json()) as DbQueryTrend;
}

/** GET /api/infra/performance/query-tags/trend — 同じタグの全てのクエリ ID を合算した推移を取得する (SuperAdmin のみ)。 */
export async function getDbQueryTagTrend(params: {
	tag: string;
	hours: number;
}): Promise<DbQueryTrend> {
	const query = new URLSearchParams({ tag: params.tag, hours: String(params.hours) });
	const res = await apiFetch(`/api/infra/performance/query-tags/trend?${query}`);
	if (!res.ok) throw new Error(`DB クエリの推移の取得に失敗しました (${res.status})`);
	return (await res.json()) as DbQueryTrend;
}

/** バックエンド HttpEndpointStatDto に対応。百分位数はヒストグラムからの見積もり。 */
export interface HttpEndpointStat {
	method: string;
	route: string;
	requestCount: number;
	errorCount: number;
	totalMs: number;
	meanMs: number;
	p50Ms: number;
	p95Ms: number;
	p99Ms: number;
	maxMs: number;
	/** 直前の同じ長さの期間 (previousSince から since まで) の件数。 */
	previousRequestCount: number;
	previousTotalMs: number;
}

export interface HttpEndpointStats {
	observedAt: string;
	since: string;
	/** 直前の同じ長さの期間の始点。遡れないときは null。 */
	previousSince: string | null;
	endpoints: HttpEndpointStat[];
}

/** GET /api/infra/performance/endpoints — 直近 hours 時間のエンドポイントごとの応答時間を取得する (SuperAdmin のみ)。 */
export async function getHttpEndpointStats(hours: number): Promise<HttpEndpointStats> {
	const res = await apiFetch(`/api/infra/performance/endpoints?hours=${hours}`);
	if (!res.ok) throw new Error(`エンドポイントの統計の取得に失敗しました (${res.status})`);
	return (await res.json()) as HttpEndpointStats;
}

/** バックエンド HttpEndpointTrendPointDto に対応。リクエストが無かった区間は meanMs・p95Ms が null。 */
export interface HttpEndpointTrendPoint {
	start: string;
	end: string;
	requestCount: number;
	errorCount: number;
	meanMs: number | null;
	p95Ms: number | null;
}

/** GET /api/infra/performance/endpoint-trend — エンドポイントの応答時間とリクエスト数の推移を取得する (SuperAdmin のみ)。 */
export async function getHttpEndpointTrend(params: {
	method: string;
	route: string;
	hours: number;
}): Promise<HttpEndpointTrendPoint[]> {
	const query = new URLSearchParams({
		method: params.method,
		route: params.route,
		hours: String(params.hours),
	});
	const res = await apiFetch(`/api/infra/performance/endpoint-trend?${query}`);
	if (!res.ok) throw new Error(`エンドポイントの推移の取得に失敗しました (${res.status})`);
	return (await res.json()) as HttpEndpointTrendPoint[];
}

/** バックエンド HttpEndpointErrorDto に対応。例外を投げずに 5xx を返したときは例外の欄が null。 */
export interface HttpEndpointError {
	id: string;
	occurredAt: string;
	path: string;
	statusCode: number;
	exceptionType: string | null;
	message: string | null;
	detail: string | null;
	traceId: string;
}

/** GET /api/infra/performance/endpoint-errors — 直近 hours 時間にエンドポイントが返した 5xx の見本を新しい順に取得する (SuperAdmin のみ)。 */
export async function getHttpEndpointErrors(params: {
	method: string;
	route: string;
	hours: number;
}): Promise<HttpEndpointError[]> {
	const query = new URLSearchParams({
		method: params.method,
		route: params.route,
		hours: String(params.hours),
	});
	const res = await apiFetch(`/api/infra/performance/endpoint-errors?${query}`);
	if (!res.ok) throw new Error(`エンドポイントのエラーの取得に失敗しました (${res.status})`);
	return (await res.json()) as HttpEndpointError[];
}

/** バックエンド WafHitMode に対応。excluded はグループの中でカウントに固定したルール。 */
export type WafHitMode = "count" | "block" | "excluded";

/** バックエンド WafHit に対応。subRule はマネージドルールグループの中のルール。 */
export interface WafHit {
	key: string;
	rule: string;
	subRule: string | null;
	mode: WafHitMode;
}

/** バックエンド WafRuleSeries に対応。Web ACL のルール 1 つで、当たりが無くても出る。 */
export interface WafRuleSeries {
	rule: string;
	total: number;
	blocked: number;
}

/** バックエンド WafBucket に対応。counts・blocked は Web ACL のルール名ごとの件数。 */
export interface WafBucket {
	start: string;
	end: string;
	counts: Record<string, number>;
	blocked: Record<string, number>;
}

/** バックエンド WafTopRow に対応。 */
export interface WafTopRow {
	rule: string;
	subRule: string | null;
	mode: WafHitMode;
	pathPrefix: string;
	clientIp: string;
	country: string | null;
	authenticated: boolean;
	count: number;
}

/** バックエンド WafSummaryDto に対応。truncated は Logs Insights が上限の行数で切り、件数の少ない組み合わせが落ちたこと。 */
export interface WafSummary {
	from: string;
	to: string;
	rules: WafRuleSeries[];
	buckets: WafBucket[];
	top: WafTopRow[];
	truncated: boolean;
}

/** バックエンド WafRequestDto に対応。log は WAF のログ 1 件の JSON (認証ヘッダーは伏せてある)。 */
export interface WafRequest {
	timestamp: string;
	action: string | null;
	method: string | null;
	uri: string | null;
	args: string | null;
	clientIp: string | null;
	country: string | null;
	userAgent: string | null;
	hits: WafHit[];
	log: string;
}

const WAF_NOT_CONFIGURED = "WAF が無い環境のため表示できません。";

/** GET /api/infra/waf — WAF のルールに当たったリクエストの推移と上位を取得する (SuperAdmin のみ)。 */
export async function getWafSummary(hours: number): Promise<WafSummary> {
	const res = await apiFetch(`/api/infra/waf?hours=${hours}`);
	if (res.status === 404) throw new Error(WAF_NOT_CONFIGURED);
	if (!res.ok) throw new Error(`WAF のログの集計に失敗しました (${res.status})`);
	return (await res.json()) as WafSummary;
}

/** GET /api/infra/waf/requests — 上位の 1 行に当たる見本のリクエストを新しい順に取得する (SuperAdmin のみ)。 */
export async function getWafRequests(params: {
	hours: number;
	row: WafTopRow;
}): Promise<WafRequest[]> {
	const query = new URLSearchParams({
		hours: String(params.hours),
		rule: params.row.rule,
		pathPrefix: params.row.pathPrefix,
		clientIp: params.row.clientIp,
	});
	if (params.row.subRule !== null) query.set("subRule", params.row.subRule);
	const res = await apiFetch(`/api/infra/waf/requests?${query}`);
	if (res.status === 404) throw new Error(WAF_NOT_CONFIGURED);
	if (!res.ok) throw new Error(`WAF のリクエストの取得に失敗しました (${res.status})`);
	return (await res.json()) as WafRequest[];
}

/** バックエンド PerformanceAnnotationDto に対応。createdBy は付けたユーザーが消えていれば null。 */
export interface PerformanceAnnotation {
	id: string;
	target: string;
	at: string;
	text: string;
	createdBy: string | null;
	createdAt: string;
}

/**
 * GET /api/infra/performance/annotations — target の行 (例: `query-tag:…`・`endpoint:GET /api/…`) の推移のグラフに
 * 収まる期間の目印を古い順に取得する (SuperAdmin のみ)。
 */
export async function getPerformanceAnnotations(params: {
	target: string;
	hours: number;
}): Promise<PerformanceAnnotation[]> {
	const query = new URLSearchParams({ target: params.target, hours: String(params.hours) });
	const res = await apiFetch(`/api/infra/performance/annotations?${query}`);
	if (!res.ok) throw new Error(`グラフの目印の取得に失敗しました (${res.status})`);
	return (await res.json()) as PerformanceAnnotation[];
}

/** POST /api/infra/performance/annotations — target の行の推移のグラフの時刻に目印を付ける (SuperAdmin のみ)。 */
export async function createPerformanceAnnotation(input: {
	target: string;
	at: string;
	text: string;
}): Promise<PerformanceAnnotation> {
	const res = await apiFetch("/api/infra/performance/annotations", {
		method: "POST",
		body: JSON.stringify(input),
	});
	if (!res.ok) throw new Error(await apiErrorMessage(res, "目印を付けられませんでした"));
	return (await res.json()) as PerformanceAnnotation;
}

/** DELETE /api/infra/performance/annotations/{id} — 目印を消す (SuperAdmin のみ)。 */
export async function deletePerformanceAnnotation(id: string): Promise<void> {
	const res = await apiFetch(`/api/infra/performance/annotations/${encodeURIComponent(id)}`, {
		method: "DELETE",
	});
	if (!res.ok) throw new Error(`目印を消せませんでした (${res.status})`);
}

/**
 * GET /api/billing/events.csv — 集計前の生イベントを CSV で取得する。
 * 絞り込みの引数は getBillingSummary と同じで、同じ母集団が返る。
 * 日時はブラウザのタイムゾーンで書き出させる。
 * 対象が多すぎて書き出せない場合、バックエンドが理由を日本語で返すのでそのまま投げる。
 */
export async function downloadBillingEventsCsv(params: {
	from?: string;
	eventTypes?: string[];
	organizationId?: string;
}): Promise<Blob> {
	const search = new URLSearchParams();
	if (params.from) search.set("from", params.from);
	if (params.eventTypes && params.eventTypes.length > 0)
		search.set("eventTypes", params.eventTypes.join(","));
	if (params.organizationId) search.set("organizationId", params.organizationId);
	search.set("timeZone", Intl.DateTimeFormat().resolvedOptions().timeZone);
	const res = await apiFetch(`/api/billing/events.csv?${search}`);
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? `課金イベントの書き出しに失敗しました (${res.status})`);
	}
	return res.blob();
}

/**
 * バックエンド CreditBalanceDto に対応。組織の全期間のクレジット付与・消費・失効・残高と
 * 付与履歴・失効履歴 (いずれも新しい順)。balanceCredits は負値になり得る。
 */
export interface CreditBalance {
	grantedCredits: number;
	consumedCredits: number;
	expiredCredits: number;
	balanceCredits: number;
	grants: CreditGrant[];
	expirations: CreditExpiration[];
}

/** バックエンド CreditGrantDto に対応。負の amountCredits は修正・取消。 */
export interface CreditGrant {
	id: string;
	amountCredits: number;
	reason: string;
	/** 外部の請求との紐付け用の請求書番号。紐付けがなければ null。 */
	invoiceNumber: string | null;
	/** 付与者の「姓 名」。 */
	grantedByUserName: string;
	createdAt: string;
	/** 未使用分の失効日時。負の付与は null。 */
	expiresAt: string | null;
}

/** バックエンド CreditExpirationDto に対応。付与 grantId の未使用分の失効。amountCredits は正の値。 */
export interface CreditExpiration {
	grantId: string;
	amountCredits: number;
	expiredAt: string;
}

/**
 * GET /api/billing/credits — 組織のクレジット残高と付与履歴を取得する。
 * organizationId は超管理者のみ自組織以外を指定でき、省略時は自組織。
 * 付与・失効の履歴 (grants / expirations) は超管理者以外では空で返る。
 */
export async function getCreditBalance(params: {
	organizationId?: string;
}): Promise<CreditBalance> {
	const search = new URLSearchParams();
	if (params.organizationId) search.set("organizationId", params.organizationId);
	const qs = search.toString();
	const res = await apiFetch(`/api/billing/credits${qs ? `?${qs}` : ""}`);
	if (!res.ok) throw new Error(`クレジット残高の取得に失敗しました (${res.status})`);
	return (await res.json()) as CreditBalance;
}

/** POST /api/billing/credits/grants — 組織へクレジットを付与する (超管理者のみ)。 */
export async function createCreditGrant(payload: {
	organizationId: string;
	amountCredits: number;
	reason: string;
	invoiceNumber?: string;
}): Promise<CreditGrant> {
	const res = await apiFetch("/api/billing/credits/grants", {
		method: "POST",
		body: JSON.stringify(payload),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? data.title ?? `クレジットの付与に失敗しました (${res.status})`);
	}
	return (await res.json()) as CreditGrant;
}

/** 上限を判定する集計期間 (JST の暦日・暦月)。 */
export type ProjectCreditLimitPeriod = "daily" | "monthly";

/** バックエンド ProjectCreditPeriodLimitDto に対応。1 つの集計期間の上限と消費。上限が null なら設定なし。 */
export interface ProjectCreditPeriodLimit {
	softLimitCredits: number | null;
	hardLimitCredits: number | null;
	usedCredits: number;
}

/**
 * バックエンド ProjectCreditLimitDto に対応。プロジェクトの日・月のクレジット上限と、それぞれの消費。
 * hardLimitReached はサーバーが停止中と判定した状態。
 */
export interface ProjectCreditLimit extends Record<
	ProjectCreditLimitPeriod,
	ProjectCreditPeriodLimit
> {
	projectId: string;
	projectName: string;
	hardLimitReached: boolean;
}

/**
 * GET /api/billing/project-limits — 組織の有効なプロジェクトごとのクレジット上限を取得する。
 * organizationId は超管理者のみ自組織以外を指定でき、省略時は自組織。
 */
export async function getProjectCreditLimits(params: {
	organizationId?: string;
}): Promise<ProjectCreditLimit[]> {
	const search = new URLSearchParams();
	if (params.organizationId) search.set("organizationId", params.organizationId);
	const qs = search.toString();
	const res = await apiFetch(`/api/billing/project-limits${qs ? `?${qs}` : ""}`);
	if (!res.ok) throw new Error(`プロジェクトの上限の取得に失敗しました (${res.status})`);
	return (await res.json()) as ProjectCreditLimit[];
}

/** バックエンド UpdateProjectCreditLimitRequest に対応。上限を null にすると設定を外す。 */
export type ProjectCreditLimitPayload = Record<
	ProjectCreditLimitPeriod,
	{ softLimitCredits: number | null; hardLimitCredits: number | null }
>;

/** PUT /api/billing/project-limits/{projectId} — プロジェクトのクレジット上限を設定する。 */
export async function updateProjectCreditLimit(
	projectId: string,
	payload: ProjectCreditLimitPayload,
): Promise<void> {
	const res = await apiFetch(`/api/billing/project-limits/${encodeURIComponent(projectId)}`, {
		method: "PUT",
		body: JSON.stringify(payload),
	});
	if (res.ok) return;
	const data = (await res.json().catch(() => ({}))) as {
		error?: string;
		errors?: Record<string, string[]>;
	};
	const validation = data.errors ? Object.values(data.errors)[0]?.[0] : undefined;
	throw new Error(data.error ?? validation ?? `上限の保存に失敗しました (${res.status})`);
}

/** バックエンド DeviceLicenseEventKind に対応。ライセンスの状態の変化の種類。 */
export type DeviceLicenseEventKind =
	| "granted"
	| "cancelRequested"
	| "cancelWithdrawn"
	| "ended"
	| "unpaid"
	| "paid";

/** バックエンド DeviceLicenseEventDto に対応。ライセンスの状態の変化 1 件。 */
export interface DeviceLicenseEvent {
	kind: DeviceLicenseEventKind;
	occurredAt: string;
	/** 操作者の「姓 名」。操作者が記録されていなければ null。 */
	actorName: string | null;
}

/** バックエンド DeviceLicenseDto に対応。デバイスのライセンス 1 本。 */
export interface DeviceLicense {
	id: string;
	/** 付与の理由。理由が導入される前の付与は空文字。 */
	reason: string;
	invoiceNumber: string | null;
	/** 次の更新日。終わったライセンスは終わった日。 */
	renewsAt: string;
	/** 解約を予約した日時。予約が無ければ null。 */
	cancelRequestedAt: string | null;
	endedAt: string | null;
	/** 支払い待ちになった更新日。支払い待ちでなければ null。 */
	unpaidSince: string | null;
	/** 結び付いて登録しているデバイスの名前。空いていれば null。 */
	deviceName: string | null;
	/** 状態の変化の履歴。新しい順。 */
	events: DeviceLicenseEvent[];
}

/** バックエンド DeviceLicenseListDto に対応。unitPriceCredits は 1 台・1 か月の額。 */
export interface DeviceLicenseList {
	unitPriceCredits: number;
	licenses: DeviceLicense[];
}

async function deviceLicenseError(res: Response, fallback: string): Promise<Error> {
	const data = await res.json().catch(() => ({}));
	return new Error(data.error ?? data.title ?? `${fallback} (${res.status})`);
}

/** GET /api/billing/device-licenses — 組織のデバイスのライセンス (超管理者のみ)。 */
export async function getDeviceLicenses(organizationId: string): Promise<DeviceLicenseList> {
	const res = await apiFetch(
		`/api/billing/device-licenses?${new URLSearchParams({ organizationId }).toString()}`,
	);
	if (!res.ok) throw await deviceLicenseError(res, "ライセンスの取得に失敗しました");
	return (await res.json()) as DeviceLicenseList;
}

/** POST /api/billing/device-licenses — 組織へライセンスを台数分付与し、残高から引く (超管理者のみ)。 */
export async function grantDeviceLicenses(payload: {
	organizationId: string;
	count: number;
	reason: string;
	invoiceNumber?: string;
}): Promise<DeviceLicense[]> {
	const res = await apiFetch("/api/billing/device-licenses", {
		method: "POST",
		body: JSON.stringify(payload),
	});
	if (!res.ok) throw await deviceLicenseError(res, "ライセンスの付与に失敗しました");
	return (await res.json()) as DeviceLicense[];
}

/** POST /api/billing/device-licenses/{id}/cancel — 次の更新日に終わるよう解約を予約する (超管理者のみ)。 */
export async function cancelDeviceLicense(id: string): Promise<DeviceLicense> {
	const res = await apiFetch(`/api/billing/device-licenses/${encodeURIComponent(id)}/cancel`, {
		method: "POST",
	});
	if (!res.ok) throw await deviceLicenseError(res, "ライセンスの解約に失敗しました");
	return (await res.json()) as DeviceLicense;
}

/** DELETE /api/billing/device-licenses/{id}/cancel — 解約の予約を取り消す (超管理者のみ)。 */
export async function withdrawDeviceLicenseCancel(id: string): Promise<DeviceLicense> {
	const res = await apiFetch(`/api/billing/device-licenses/${encodeURIComponent(id)}/cancel`, {
		method: "DELETE",
	});
	if (!res.ok) throw await deviceLicenseError(res, "解約の取り消しに失敗しました");
	return (await res.json()) as DeviceLicense;
}

/** バックエンド SharedVolumeState に対応。 */
export type SharedVolumeState = "creating" | "ready" | "deleting" | "error";

/** 共有ボリュームをマウントしている環境 1 件。 */
export interface SharedVolumeAttachedEnvironment {
	id: string;
	name: string;
}

/**
 * バックエンド SharedVolumeDto に対応。組織直下の共有ボリューム。measuredBytes / measuredAt は
 * 定期スイープによる使用容量の概算 (未計測なら null) で、課金には使わない。
 */
export interface SharedVolume {
	id: string;
	name: string;
	state: SharedVolumeState;
	/** 環境コンテナ内でのマウント先 (/mnt/volumes/<名前>)。 */
	mountPath: string;
	measuredBytes: number | null;
	measuredAt: string | null;
	createdAt: string;
	attachedEnvironments: SharedVolumeAttachedEnvironment[];
}

/** GET /api/volumes — 組織の共有ボリューム一覧を名前順で取得する。 */
export async function listSharedVolumes(): Promise<SharedVolume[]> {
	const res = await apiFetch("/api/volumes");
	if (!res.ok) throw new Error(`共有ボリュームの取得に失敗しました (${res.status})`);
	return (await res.json()) as SharedVolume[];
}

/**
 * GET /api/environments/{id}/volumes — 環境にマウントできる共有ボリューム (環境の組織のもの) を取得する。
 * SuperAdmin が他組織の環境を編集するときも、その環境の組織の一覧が返る。
 */
export async function listEnvironmentSharedVolumes(environmentId: string): Promise<SharedVolume[]> {
	const res = await apiFetch(`/api/environments/${encodeURIComponent(environmentId)}/volumes`);
	if (!res.ok) throw new Error(`共有ボリュームの取得に失敗しました (${res.status})`);
	return (await res.json()) as SharedVolume[];
}

/**
 * POST /api/volumes — 共有ボリュームを作成する。実体の用意はバックグラウンドで進むため
 * 作成直後は state が "creating" で、一覧のポーリングで "ready" になるのを待つ。
 * 名前重複時は 409 のメッセージを投げる。
 */
export async function createSharedVolume(name: string): Promise<SharedVolume> {
	const res = await apiFetch("/api/volumes", {
		method: "POST",
		body: JSON.stringify({ name }),
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? "この名前の共有ボリュームはすでに存在します。");
	}
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(
			data.error ?? data.title ?? `共有ボリュームの作成に失敗しました (${res.status})`,
		);
	}
	return (await res.json()) as SharedVolume;
}

/**
 * DELETE /api/volumes/{volumeId} — 共有ボリュームの削除を受け付ける (データは失われる)。
 * マウント中の環境がある場合は 409 が返り、その理由をエラーメッセージとして投げる。
 */
export async function deleteSharedVolume(volumeId: string): Promise<void> {
	const res = await apiFetch(`/api/volumes/${encodeURIComponent(volumeId)}`, {
		method: "DELETE",
	});
	if (res.status === 409) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error ?? "マウント中のエージェントがあるため削除できません。");
	}
	if (!res.ok) throw new Error(`共有ボリュームの削除に失敗しました (${res.status})`);
}

/** バックエンド RunnerVolumePhases に対応。デバイスの写しの同期の段階。 */
export type DeviceSharedVolumePhase =
	| "idle"
	| "pulling"
	| "pushing"
	| "pushPending"
	| "stoppedNoSpace"
	| "failed";

/** バックエンド RunnerVolumeChangeKinds に対応。写しの手元の変更 (取り込んだときとの比べ)。 */
export type DeviceSharedVolumeChangeKind = "modified" | "added" | "deleted" | "unsupported";

/** バックエンド EnvironmentSharedVolumeDeviceDto に対応。 */
export interface DeviceSharedVolumeDevice {
	name: string;
	online: boolean;
	/** デバイスの登録が解除されている (命令は届かないが、ボリュームは送らずに外せる)。 */
	revoked: boolean;
	/** ランナーの版が写しを扱える。 */
	runnerSupported: boolean;
	/** 命令を送れない理由 (登録の解除・オフライン・ランナーが古い)。送れるなら null。 */
	unavailableReason: string | null;
}

/** バックエンド SharedVolumeListingDto に対応。クラウドの一覧のメタ。 */
export interface DeviceSharedVolumeListing {
	generation: number;
	/** 全体を 1 回以上取った (false なら取り込む部分だけ)。 */
	complete: boolean;
	entryCount: number;
	totalBytes: number;
	unsupportedCount: number;
	listedAt: string | null;
	/** 一覧を取り直している (書き換えている) 最中。終われば generation・listedAt が変わりうる。 */
	refreshing: boolean;
	/** 最後の取り直しが失敗した理由。成功すれば null に戻る。 */
	refreshError: string | null;
}

/** バックエンド EnvironmentVolumeStateDto に対応。ランナーが最後に報告した写しの状態。 */
export interface DeviceSharedVolumeState {
	phase: DeviceSharedVolumePhase;
	filesDone: number;
	filesTotal: number;
	bytesDone: number;
	bytesTotal: number;
	lastError: string | null;
	lastPulledAt: string | null;
	lastPushedAt: string | null;
	/** 起動中の agent に写しが見えておらず、再起動すると見える。 */
	restartRequired: boolean;
	scannedAt: string | null;
	/**
	 * 最後に数えた手元の変更のうち、反映するものの数 (対象外は除く。changesTruncated なら、一覧に載らなかった対象外は
	 * 除けない)。
	 */
	unpushedChangeCount: number;
	/** 変更の一覧が上限で打ち切られている (ディレクトリごとの数は一覧に載った分だけ)。 */
	changesTruncated: boolean;
	updatedAt: string | null;
	/** 写しを作るときに、写しの置き場に元からあった同じ名前のものを移した先の名前。共有ボリュームには反映しない。 */
	movedAsideTo: string | null;
}

/** バックエンド EnvironmentSharedVolumeDto に対応。デバイスの環境に付いた共有ボリューム 1 件。 */
export interface DeviceSharedVolume {
	volumeId: string;
	name: string;
	mountPath: string;
	volumeState: SharedVolumeState;
	/** クラウドの一覧。まだ取っておらず、取り直してもいなければ null。 */
	listing: DeviceSharedVolumeListing | null;
	state: DeviceSharedVolumeState;
}

/** バックエンド EnvironmentSharedVolumesDto に対応。 */
export interface DeviceSharedVolumes {
	device: DeviceSharedVolumeDevice;
	/** 環境が起動中か起動の途中 (デバイスがオンラインのときだけ分かる)。 */
	running: boolean;
	volumes: DeviceSharedVolume[];
}

/** バックエンド SharedVolumeChild に対応。クラウドの一覧に規則と手元の変更を合わせた 1 件。 */
export interface DeviceSharedVolumeEntry {
	name: string;
	/** ボリュームの根からの相対パス。 */
	path: string;
	kind: "file" | "directory" | "symlink" | "other";
	size: number;
	inclusion: "included" | "excluded" | "partial";
	/** この名前の手元の変更。手元で作ったディレクトリは、変更が下にだけあるので null。 */
	localChange: DeviceSharedVolumeChangeKind | null;
	/** 下にある、反映する手元の変更の数 (ディレクトリのとき。対象外は数えない)。 */
	changedDescendants: number;
	/** 下にある、手元の対象外 (symlink・FIFO など) の数。反映はしないが、写しを消せば消える。 */
	unsupportedDescendants: number;
	/** 通常のファイル・ディレクトリでないので写さない。 */
	unsupported: boolean;
	/** クラウドの一覧に無く、手元にだけある。 */
	localOnly: boolean;
	/** この名前が手元に無い (変更が削除)。 */
	locallyDeleted: boolean;
}

/** バックエンド SharedVolumeChildrenDto に対応。 */
export interface DeviceSharedVolumeChildren {
	path: string;
	entries: DeviceSharedVolumeEntry[];
	/** 続きがあれば、次の要求の cursor に渡す値。 */
	nextCursor: string | null;
	listing: DeviceSharedVolumeListing | null;
}

/** バックエンド SharedVolumeSyncDto に対応。規則の保存の後にデバイスへ取り込みを送ったか。 */
export interface DeviceSharedVolumeSync {
	sent: boolean;
	/** 送らなかった理由 (停止中・オフライン・ランナーが古い・デバイスでの失敗)。 */
	reason: string | null;
	/** デバイスで失敗した (停止中・オフラインなど、後で取り込むものは false)。 */
	failed: boolean;
}

function deviceSharedVolumesPath(environmentId: string, rest = ""): string {
	return `/api/environments/${encodeURIComponent(environmentId)}/shared-volumes${rest}`;
}

function deviceSharedVolumePath(environmentId: string, volumeId: string, rest: string): string {
	return deviceSharedVolumesPath(environmentId, `/${encodeURIComponent(volumeId)}${rest}`);
}

async function throwSharedVolumeError(response: Response, fallback: string): Promise<never> {
	throw new ApiStatusError(await apiErrorMessage(response, fallback), response.status);
}

/** GET /api/environments/{id}/shared-volumes — デバイスの環境に付いた共有ボリュームと写しの状態 (クラウドの環境は 400)。 */
export async function fetchDeviceSharedVolumes(
	environmentId: string,
): Promise<DeviceSharedVolumes> {
	const res = await apiFetch(deviceSharedVolumesPath(environmentId));
	if (!res.ok) await throwSharedVolumeError(res, "共有ボリュームの状態を取得できませんでした");
	return (await res.json()) as DeviceSharedVolumes;
}

/**
 * POST /api/environments/{id}/shared-volumes/scan — 手元の変更を数え直して全体を返す。オフライン・ランナーが古いときは
 * 保存済みの状態を返す。
 */
export async function scanDeviceSharedVolumes(environmentId: string): Promise<DeviceSharedVolumes> {
	const res = await apiFetch(deviceSharedVolumesPath(environmentId, "/scan"), { method: "POST" });
	if (!res.ok) await throwSharedVolumeError(res, "手元の変更を確かめられませんでした");
	return (await res.json()) as DeviceSharedVolumes;
}

/** GET .../{volumeId}/children — ディレクトリの直下 (1000 件まで)。続きは cursor に nextCursor を渡す。 */
export async function fetchDeviceSharedVolumeChildren(
	environmentId: string,
	volumeId: string,
	path: string,
	cursor: string | null = null,
): Promise<DeviceSharedVolumeChildren> {
	const query = new URLSearchParams({ path });
	if (cursor !== null) query.set("cursor", cursor);
	const res = await apiFetch(deviceSharedVolumePath(environmentId, volumeId, `/children?${query}`));
	if (!res.ok) await throwSharedVolumeError(res, "共有ボリュームの中身を取得できませんでした");
	return (await res.json()) as DeviceSharedVolumeChildren;
}

/** バックエンド CloudSharedVolumeDto に対応。クラウドの環境に付いた共有ボリューム。 */
export interface CloudSharedVolume {
	volumeId: string;
	name: string;
	mountPath: string;
	/** 今の環境に mount されている。起動の後に付けたものは、次の起動まで false。 */
	mounted: boolean;
}

/** バックエンド CloudSharedVolumeEntry に対応。symlink・other は開けない。 */
export interface CloudSharedVolumeEntry {
	name: string;
	/** ボリュームの根からの相対パス。 */
	path: string;
	kind: "file" | "directory" | "symlink" | "other";
	size: number;
}

/** バックエンド CloudSharedVolumeChildrenDto に対応。 */
export interface CloudSharedVolumeChildren {
	path: string;
	entries: CloudSharedVolumeEntry[];
	/** 続きがあれば、次の要求の cursor に渡す値。 */
	nextCursor: string | null;
}

function cloudSharedVolumesPath(environmentId: string, rest = ""): string {
	return `/api/environments/${encodeURIComponent(environmentId)}/cloud-shared-volumes${rest}`;
}

/** GET /api/environments/{id}/cloud-shared-volumes — クラウドの環境に付いた共有ボリューム (停止中は 409、デバイスの環境は 400)。 */
export async function fetchCloudSharedVolumes(environmentId: string): Promise<CloudSharedVolume[]> {
	const res = await apiFetch(cloudSharedVolumesPath(environmentId));
	if (!res.ok) await throwSharedVolumeError(res, "共有ボリュームの一覧を取得できませんでした");
	return (await res.json()) as CloudSharedVolume[];
}

/** GET /api/environments/{id}/cloud-shared-volumes/{volumeId}/children — ディレクトリの直下 (mount されていなければ 409)。 */
export async function fetchCloudSharedVolumeChildren(
	environmentId: string,
	volumeId: string,
	path: string,
	cursor: string | null = null,
): Promise<CloudSharedVolumeChildren> {
	const query = new URLSearchParams({ path });
	if (cursor !== null) query.set("cursor", cursor);
	const res = await apiFetch(
		cloudSharedVolumesPath(environmentId, `/${encodeURIComponent(volumeId)}/children?${query}`),
	);
	if (!res.ok) await throwSharedVolumeError(res, "共有ボリュームの中身を取得できませんでした");
	return (await res.json()) as CloudSharedVolumeChildren;
}

async function postCloudSharedVolume(
	environmentId: string,
	volumeId: string,
	operation: string,
	body: unknown,
	fallback: string,
): Promise<Response> {
	const res = await apiFetch(
		cloudSharedVolumesPath(environmentId, `/${encodeURIComponent(volumeId)}/${operation}`),
		{ method: "POST", body: JSON.stringify(body) },
	);
	if (!res.ok) await throwSharedVolumeError(res, fallback);
	return res;
}

/**
 * POST .../cloud-shared-volumes/{volumeId}/create-folder — フォルダを作る (根からのパス。無い親も作る。既にあれば 409。
 * allowExisting なら、既にあるのがフォルダのときは成功にする)。
 */
export async function createCloudSharedVolumeFolder(
	environmentId: string,
	volumeId: string,
	path: string,
	allowExisting = false,
): Promise<void> {
	await postCloudSharedVolume(
		environmentId,
		volumeId,
		"create-folder",
		{ path, allowExisting },
		"フォルダを作成できませんでした",
	);
}

/** POST .../cloud-shared-volumes/{volumeId}/create-file — 空のファイルを作る (既にあれば 409)。 */
export async function createCloudSharedVolumeFile(
	environmentId: string,
	volumeId: string,
	path: string,
): Promise<void> {
	await postCloudSharedVolume(
		environmentId,
		volumeId,
		"create-file",
		{ path },
		"ファイルを作成できませんでした",
	);
}

/** POST .../cloud-shared-volumes/{volumeId}/move — 同じボリュームの中で移す・名前を変える (移動先にあれば 409)。 */
export async function moveCloudSharedVolumeEntry(
	environmentId: string,
	volumeId: string,
	from: string,
	to: string,
): Promise<void> {
	await postCloudSharedVolume(
		environmentId,
		volumeId,
		"move",
		{ from, to },
		"移動できませんでした",
	);
}

/** POST .../cloud-shared-volumes/{volumeId}/delete — 消す (無いものは飛ばす)。 */
export async function deleteCloudSharedVolumeEntries(
	environmentId: string,
	volumeId: string,
	paths: string[],
): Promise<void> {
	await postCloudSharedVolume(environmentId, volumeId, "delete", { paths }, "削除できませんでした");
}

/** 写しの手元の変更 1 件。 */
export interface DeviceSharedVolumeChange {
	/** ボリュームの根からの相対パス。 */
	path: string;
	kind: DeviceSharedVolumeChangeKind;
}

/** バックエンド SharedVolumeChangesDto に対応。最後に数えた手元の変更のうち、範囲の下のもの。 */
export interface DeviceSharedVolumeChanges {
	/** 反映する変更 (書き換え・手元で作ったもの・削除) の数。対象外は含めない。 */
	total: number;
	modified: number;
	added: number;
	deleted: number;
	unsupported: number;
	/** 変更の一覧が打ち切られていて、数は一覧に載った分だけ。 */
	truncated: boolean;
	/** パスの順の先頭 (limit 件まで)。 */
	items: DeviceSharedVolumeChange[];
}

/** GET .../{volumeId}/changes — 最後に数えた手元の変更のうち、path (空ならボリューム全体) の下のもの。 */
export async function fetchDeviceSharedVolumeChanges(
	environmentId: string,
	volumeId: string,
	path: string,
	limit: number,
): Promise<DeviceSharedVolumeChanges> {
	const query = new URLSearchParams({ path, limit: String(limit) });
	const res = await apiFetch(deviceSharedVolumePath(environmentId, volumeId, `/changes?${query}`));
	if (!res.ok) await throwSharedVolumeError(res, "反映する変更を取得できませんでした");
	return (await res.json()) as DeviceSharedVolumeChanges;
}

/** PUT .../{volumeId}/rules — パス (根は空文字) を取り込むか決める。起動中なら保存の後に取り込みを送る。 */
export async function saveDeviceSharedVolumeRule(
	environmentId: string,
	volumeId: string,
	path: string,
	include: boolean,
): Promise<DeviceSharedVolumeSync> {
	const res = await apiFetch(deviceSharedVolumePath(environmentId, volumeId, "/rules"), {
		method: "PUT",
		body: JSON.stringify({ path, include }),
	});
	if (!res.ok) await throwSharedVolumeError(res, "取り込む範囲を保存できませんでした");
	return ((await res.json()) as { sync: DeviceSharedVolumeSync }).sync;
}

/**
 * POST .../{volumeId}/listing — クラウドの一覧の取り直しを裏で始め、今の一覧 (refreshing) をすぐ返す (202)。1 分以内に
 * 全体を取っていれば取り直さずにその一覧を返す (200)。終わったかは状態の listing.refreshing で分かる。
 */
export async function refreshDeviceSharedVolumeListing(
	environmentId: string,
	volumeId: string,
): Promise<DeviceSharedVolumeListing> {
	const res = await apiFetch(deviceSharedVolumePath(environmentId, volumeId, "/listing"), {
		method: "POST",
	});
	if (!res.ok) await throwSharedVolumeError(res, "共有ボリュームの一覧を取り直せませんでした");
	return (await res.json()) as DeviceSharedVolumeListing;
}

/** POST .../push — 手元の変更を共有ボリュームに反映させる。paths は根からの相対パスの部分木で、null なら全体。 */
export async function pushDeviceSharedVolume(
	environmentId: string,
	volumeId: string,
	paths: string[] | null,
): Promise<void> {
	const res = await apiFetch(deviceSharedVolumesPath(environmentId, "/push"), {
		method: "POST",
		body: JSON.stringify({ volumeId, paths }),
	});
	if (!res.ok) await throwSharedVolumeError(res, "反映を始められませんでした");
}

/** 共有ボリューム (デバイスは写し) に在るもの。directory はディレクトリか (symlink は含めない)。 */
export interface SharedVolumeExistingPath {
	path: string;
	directory: boolean;
}

/**
 * POST .../{volumeId}/existing-paths — 共有ボリューム (デバイスは写し、クラウドは mount 先) に同じ名前のものが既にあるもの
 * (根からの相対パス) を返す。
 */
export async function existingSharedVolumePaths(
	environmentId: string,
	volumeId: string,
	paths: string[],
): Promise<SharedVolumeExistingPath[]> {
	const res = await apiFetch(deviceSharedVolumePath(environmentId, volumeId, "/existing-paths"), {
		method: "POST",
		body: JSON.stringify({ paths }),
	});
	if (!res.ok) await throwSharedVolumeError(res, "共有ボリュームを確かめられませんでした");
	return ((await res.json()) as { entries: SharedVolumeExistingPath[] }).entries;
}

/**
 * POST .../{volumeId}/copy-from-home — ~ のもの (~ からの相対パス) を、共有ボリューム (デバイスは写し、クラウドは mount 先) の
 * destinationPath (根からの相対パス) へ同じ名前でコピーする要求を出し、copyId を返す。コピーはサーバの裏で進むので、終わりは
 * {@link waitForSharedVolumeHomeCopy} で待つ。
 * 同じ環境で別のコピーが進んでいれば 409。
 */
export async function copyHomeEntriesToSharedVolume(
	environmentId: string,
	volumeId: string,
	sources: string[],
	destinationPath: string,
): Promise<{ copyId: string }> {
	const res = await apiFetch(deviceSharedVolumePath(environmentId, volumeId, "/copy-from-home"), {
		method: "POST",
		body: JSON.stringify({ sources, destinationPath }),
	});
	if (!res.ok) await throwSharedVolumeError(res, "共有ボリュームにコピーできませんでした");
	return (await res.json()) as { copyId: string };
}

/**
 * GET .../{volumeId}/copy-from-home/{copyId} をポーリングし、~ からのコピーが終わるまで待つ。置けなかった・途中で失敗したときは
 * サーバの理由で投げる (事前の確認で断ったときは何もコピーしない。コピーの途中で失敗すると、それまでのものは残る)。
 */
export async function waitForSharedVolumeHomeCopy(
	environmentId: string,
	volumeId: string,
	copyId: string,
): Promise<void> {
	await waitForBackgroundTask(
		deviceSharedVolumePath(
			environmentId,
			volumeId,
			`/copy-from-home/${encodeURIComponent(copyId)}`,
		),
		"コピーの状態の取得に失敗しました",
		// 置けなかった理由を画面に出すため、要求で断ったとき (409) と同じ形にする。
		(error) => new ApiStatusError(error ?? "共有ボリュームにコピーできませんでした。", 409),
		HOME_COPY_POLL_MAX_CONSECUTIVE_FAILURES,
	);
}

/**
 * POST .../{volumeId}/discard — 範囲の取り込みをやめ (規則を保存し)、手元の写しから消す。消せなければ規則も変えない。
 * paths は根からの相対パスの部分木で、null なら全体。
 */
export async function discardDeviceSharedVolumeCopy(
	environmentId: string,
	volumeId: string,
	paths: string[] | null,
): Promise<void> {
	const res = await apiFetch(deviceSharedVolumePath(environmentId, volumeId, "/discard"), {
		method: "POST",
		body: JSON.stringify({ paths }),
	});
	if (!res.ok) await throwSharedVolumeError(res, "取り込みをやめられませんでした");
}

export interface DeviceCreator {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
}

async function throwDeviceApiError(response: Response, fallback: string): Promise<never> {
	throw new Error(await apiErrorMessage(response, fallback));
}

/** バックエンド RunnersEndpoints.ToResponse に対応。 */
export interface Runner {
	id: string;
	name: string;
	status: RunnerRegistrationStatus;
	lastSeenAt: string | null;
	online: boolean;
	runnerStatus: string | null;
	runnerStatusMessage: string | null;
	runnerVersion: string | null;
	/** デバイスの自己診断の警告 (最後のハートビートの報告)。 */
	diagnostics: RunnerDiagnostic[];
	/** ランナーが Izanagi の配る版より古く、画面から更新できる。 */
	updateAvailable: boolean;
	/** ランナーが最低の版より古く、更新できる。更新するよう案内する。 */
	updateRequired: boolean;
	revokedAt: string | null;
	createdAt: string;
	createdBy: DeviceCreator;
	/** このデバイスに作った環境 (停止中を含む、削除していないもの) の数。 */
	environmentCount: number;
	/** 結び付いたライセンス。結び付いていなければ null。 */
	license: RunnerLicense | null;
	/** デバイスが最後に報告したロボット。報告が無ければ null。 */
	robot: DeviceRobot | null;
}

/** バックエンド RunnerLicenseDto に対応。 */
export interface RunnerLicense {
	renewsAt: string;
	/** 解約を予約した日時。予約があれば更新日にこのデバイスの登録を解除する。 */
	cancelRequestedAt: string | null;
	/** 支払い待ちになった更新日。支払い待ちの間はこのデバイスの環境を止める。 */
	unpaidSince: string | null;
}

/** バックエンド DeviceLicenseSummaryDto に対応。 */
export interface DeviceLicenseSummary {
	/** 終わっていないライセンスの本数。 */
	licenseCount: number;
	/** 登録しているデバイスの数 (解除済み・トークンを受け取らずに期限が切れたものを除く)。 */
	registeredCount: number;
	freeCount: number;
}

/** GET /api/runners/licenses — 自組織のライセンスの本数・登録しているデバイスの数・空いているライセンスの数。 */
export async function getDeviceLicenseSummary(): Promise<DeviceLicenseSummary> {
	const response = await apiFetch("/api/runners/licenses");
	if (!response.ok) return throwDeviceApiError(response, "ライセンスの数の取得に失敗しました。");
	return (await response.json()) as DeviceLicenseSummary;
}

/** デバイスの自己診断の警告 1 つ (linger が無効・CDI の spec が古いなど)。 */
export interface RunnerDiagnostic {
	code: string;
	message: string;
}

/** GET /api/runners — 自組織のデバイスを作成日時の新しい順に取得する。承認前の登録要求は含まない。 */
export async function listRunners(): Promise<Runner[]> {
	const response = await apiFetch("/api/runners");
	if (!response.ok) return throwDeviceApiError(response, "デバイスの取得に失敗しました。");
	return (await response.json()) as Runner[];
}

export async function revokeRunner(id: string): Promise<Runner> {
	const response = await apiFetch(`/api/runners/${encodeURIComponent(id)}/revoke`, {
		method: "POST",
	});
	if (!response.ok) return throwDeviceApiError(response, "デバイスの登録の解除に失敗しました。");
	return (await response.json()) as Runner;
}

/** POST /api/runners/{id}/update の応答。ランナーは新しい版で起動し直す。 */
export interface RunnerUpdateResult {
	version: string;
	previousVersion: string;
}

/** 更新の結果。`timedOut` は Izanagi が待ちきれなかった (504) だけで、ランナーは更新を続けている。 */
export type RunnerUpdateOutcome = ({ timedOut: false } & RunnerUpdateResult) | { timedOut: true };

/**
 * POST /api/runners/{id}/update — Izanagi が配る版にランナーを更新する。ランナーが取得と読み込みを終えるまで (5 分まで) 待つ。
 * ランナーは結果を返した後に起動し直すので、数十秒オフラインになる。
 */
export async function updateRunner(id: string): Promise<RunnerUpdateOutcome> {
	const response = await apiFetch(`/api/runners/${encodeURIComponent(id)}/update`, {
		method: "POST",
	});
	if (response.status === 504) return { timedOut: true };
	if (!response.ok)
		throw new ApiStatusError(
			await apiErrorMessage(response, "ランナーの更新に失敗しました。"),
			response.status,
		);
	return { timedOut: false, ...((await response.json()) as RunnerUpdateResult) };
}

/** POST /api/runners/{id}/retry-prepare の応答。 */
export interface RunnerRetryPrepareResult {
	/** 準備に失敗して待っていたので試し直した。準備中・準備済みなら false。 */
	retried: boolean;
	runner: Runner;
}

/** POST /api/runners/{id}/retry-prepare — 環境イメージの取得を、自動の試し直し (10 分後) を待たずにやり直させる。 */
export async function retryRunnerPrepare(id: string): Promise<RunnerRetryPrepareResult> {
	const response = await apiFetch(`/api/runners/${encodeURIComponent(id)}/retry-prepare`, {
		method: "POST",
	});
	if (!response.ok)
		return throwDeviceApiError(response, "環境イメージの取得のやり直しに失敗しました。");
	return (await response.json()) as RunnerRetryPrepareResult;
}

/** ランナーが作った登録要求。管理者がデバイスに出た確認コードで探し、名前とアカウント名を確かめて承認する。 */
export interface RunnerRegistrationRequest {
	/** 表示の形 (XXXX-XXXX) の確認コード。 */
	userCode: string;
	name: string;
	/** デバイスの Linux のアカウント名。ランナーが送らなければ null。 */
	accountName: string | null;
	expiresAt: string;
}

const RUNNER_REGISTRATION_RATE_LIMITED =
	"試行が多すぎます。しばらく待ってからもう一度お試しください。";

/**
 * 登録要求の取得・承認の失敗。404 (無い・期限切れ・ほかの組織の要求) と 429 (試行が多すぎる) は本文に頼らず文言を決め、
 * そのほかは backend の文言を使う。
 */
async function throwRunnerRegistrationError(response: Response, fallback: string): Promise<never> {
	const message =
		response.status === 404
			? "確認コードが見つからないか、期限が切れています。デバイスに出ている確認コードを確かめ、期限が切れていれば登録用のコードを発行し直してデバイスで入れ直してください。この組織で発行した登録用のコードを使ったデバイスだけを登録できます。"
			: response.status === 429
				? RUNNER_REGISTRATION_RATE_LIMITED
				: await apiErrorMessage(response, fallback);
	throw new ApiStatusError(message, response.status);
}

/** GET /api/runners/registrations/{userCode} — 期限内で未承認の登録要求を確認コードで探す。 */
export async function getRunnerRegistrationRequest(
	userCode: string,
): Promise<RunnerRegistrationRequest> {
	const response = await apiFetch(`/api/runners/registrations/${encodeURIComponent(userCode)}`);
	if (!response.ok)
		return throwRunnerRegistrationError(response, "確認コードの確認に失敗しました。");
	return (await response.json()) as RunnerRegistrationRequest;
}

/** POST /api/runners/registrations/{userCode}/approve — 承認した人の組織にデバイスを登録する。 */
export async function approveRunnerRegistrationRequest(userCode: string): Promise<Runner> {
	const response = await apiFetch(
		`/api/runners/registrations/${encodeURIComponent(userCode)}/approve`,
		{ method: "POST" },
	);
	if (!response.ok) return throwRunnerRegistrationError(response, "デバイスの登録に失敗しました。");
	return (await response.json()) as Runner;
}

/** バックエンド RunnersEndpoints.IssueEnrollmentCodeAsync に対応。 */
export interface RunnerEnrollmentCode {
	/** izr_enroll_…。ランナーの IZANAGI_ENROLLMENT_CODE に渡し、登録の要求を発行した組織に固定する。 */
	code: string;
	/** 発行から 10 分。1 台の登録にだけ使える。 */
	expiresAt: string;
}

/** POST /api/runners/enrollment-codes — 自組織に結び付く登録用のコードを発行する。平文はこの応答にしか無い。 */
export async function issueRunnerEnrollmentCode(): Promise<RunnerEnrollmentCode> {
	const response = await apiFetch("/api/runners/enrollment-codes", { method: "POST" });
	if (response.status === 429) throw new ApiStatusError(RUNNER_REGISTRATION_RATE_LIMITED, 429);
	if (!response.ok) return throwDeviceApiError(response, "登録用のコードの発行に失敗しました。");
	return (await response.json()) as RunnerEnrollmentCode;
}

export type RunnerArch = "amd64" | "arm64";

/** 503 の本文が、設定 (公開 URL・ランナーの版・環境イメージの版) が無く配れないことを表すか。 */
async function isRunnerDownloadNotConfigured(response: Response): Promise<boolean> {
	const body: unknown = await response.json().catch(() => null);
	const type = typeof body === "object" && body !== null ? (body as { type?: unknown }).type : null;
	return (
		type === "device_registration_unavailable" ||
		type === "runner_image_not_configured" ||
		type === "environment_images_not_configured"
	);
}

/** バックエンド RunnerEnvironmentSourcesDownload.DescribeAsync に対応。デバイスに配る環境イメージの対応するソースの tar。 */
export interface RunnerEnvironmentSources {
	/** 環境イメージの版。 */
	version: string;
	sources: { fileName: string; size: number };
}

/**
 * GET /api/runners/environment-images — 環境イメージの版が未設定で配れないときは null。
 * ほかの失敗 (ソースの tar が欠けているなどの 503 を含む) は理由を投げ、画面は再試行を出す。
 */
export async function getRunnerEnvironmentSources(): Promise<RunnerEnvironmentSources | null> {
	const response = await apiFetch("/api/runners/environment-images");
	if (response.status === 503 && (await isRunnerDownloadNotConfigured(response.clone())))
		return null;
	if (!response.ok)
		return throwDeviceApiError(response, "環境イメージのソースコードの情報の取得に失敗しました。");
	return (await response.json()) as RunnerEnvironmentSources;
}

/** 環境イメージ (agent・browser・secrets-proxy・robot) の対応するソースの tar (アーキテクチャ共通)。本体は backend が流す。 */
export function runnerEnvironmentSourcesUrl(): string {
	return `${API_URL}/api/runners/environment-images/sources`;
}

/** 環境イメージの全アーキテクチャ・全イメージのライセンス表示 (THIRD-PARTY-NOTICES) をまとめた tar。本体は backend が流す。 */
export function runnerEnvironmentNoticesUrl(): string {
	return `${API_URL}/api/runners/environment-images/notices`;
}

/** バックエンドの GET /api/runners/package に対応。デバイスに入れる .deb の情報。 */
export interface RunnerPackage {
	version: string;
	/** ダウンロードできるアーキテクチャ。 */
	architectures: RunnerArch[];
}

/**
 * GET /api/runners/package — 公開 URL かランナーの版が未設定で配れないときは null。
 * ほかの失敗 (配布物が欠けているなどの一時的な 503 を含む) は理由を投げ、画面は再試行を出す。
 */
export async function getRunnerPackage(): Promise<RunnerPackage | null> {
	const response = await apiFetch("/api/runners/package");
	if (response.status === 503 && (await isRunnerDownloadNotConfigured(response.clone())))
		return null;
	if (!response.ok) return throwDeviceApiError(response, ".deb の情報の取得に失敗しました。");
	return (await response.json()) as RunnerPackage;
}

/** ブラウザのダウンロードに渡す URL。本体は backend が流す。 */
export function runnerPackageDownloadUrl(arch: RunnerArch): string {
	return `${API_URL}/api/runners/package/${arch}`;
}

/** .deb に入れた Podman 一式 (GPL の passt を含む)・ランナーのイメージの Alpine のパッケージと .deb の組み立てのスクリプトのソースの tar。 */
export function runnerPackageSourcesUrl(): string {
	return `${API_URL}/api/runners/package/sources`;
}

/**
 * バックエンド SecretDto に対応。環境 (エージェント) ごとのシークレット。値は作成後に読み出せないため含まれない。
 * 環境の bash / ターミナルには name を環境変数名、プレースホルダを値として注入され、
 * allowedHosts へのリクエスト送信時に proxy が実値へ置き換える。
 */
export interface Secret {
	id: string;
	name: string;
	allowedHosts: string[];
	createdAt: string;
	updatedAt: string;
}

function secretsPath(environmentId: string): string {
	return `/api/environments/${encodeURIComponent(environmentId)}/secrets`;
}

/** GET /api/environments/{id}/secrets — 環境のシークレット一覧を名前順で取得する。 */
export async function listSecrets(environmentId: string): Promise<Secret[]> {
	const res = await apiFetch(secretsPath(environmentId));
	if (!res.ok) throw new Error(`シークレットの取得に失敗しました (${res.status})`);
	return (await res.json()) as Secret[];
}

async function readSecretError(res: Response, fallback: string): Promise<string> {
	const data = await res.json().catch(() => ({}));
	return data.error ?? data.title ?? fallback;
}

/** POST /api/environments/{id}/secrets — シークレットを作成する。名前重複時は 409 のメッセージを投げる。 */
export async function createSecret(
	environmentId: string,
	input: { name: string; value: string; allowedHosts: string[] },
): Promise<Secret> {
	const res = await apiFetch(secretsPath(environmentId), {
		method: "POST",
		body: JSON.stringify(input),
	});
	if (!res.ok) {
		throw new Error(await readSecretError(res, `シークレットの作成に失敗しました (${res.status})`));
	}
	return (await res.json()) as Secret;
}

/**
 * PUT /api/environments/{id}/secrets/{secretId} — 許可ホストと (指定時のみ) 値を更新する。value を省くと値は変わらない。
 */
export async function updateSecret(
	environmentId: string,
	secretId: string,
	input: { value?: string; allowedHosts: string[] },
): Promise<Secret> {
	const res = await apiFetch(`${secretsPath(environmentId)}/${encodeURIComponent(secretId)}`, {
		method: "PUT",
		body: JSON.stringify(input),
	});
	if (!res.ok) {
		throw new Error(await readSecretError(res, `シークレットの更新に失敗しました (${res.status})`));
	}
	return (await res.json()) as Secret;
}

/** DELETE /api/environments/{id}/secrets/{secretId} — シークレットを削除する。 */
export async function deleteSecret(environmentId: string, secretId: string): Promise<void> {
	const res = await apiFetch(`${secretsPath(environmentId)}/${encodeURIComponent(secretId)}`, {
		method: "DELETE",
	});
	if (!res.ok) throw new Error(`シークレットの削除に失敗しました (${res.status})`);
}

export type CliLoginRequestStatus = "pending" | "approved" | "denied" | "expired";

/** CLI のブラウザ承認ログインのリクエスト。ユーザーコードは CLI にも表示される。 */
export interface CliLoginRequest {
	userCode: string;
	clientName: string;
	createdAt: string;
	expiresAt: string;
	status: CliLoginRequestStatus;
}

export async function getCliLoginRequest(userCode: string): Promise<CliLoginRequest> {
	const res = await apiFetch(`/api/auth/cli/requests/${encodeURIComponent(userCode)}`);
	if (!res.ok)
		throw new Error(await apiErrorMessage(res, "ログインリクエストの取得に失敗しました"));
	return (await res.json()) as CliLoginRequest;
}

export async function decideCliLoginRequest(userCode: string, approve: boolean): Promise<void> {
	const action = approve ? "approve" : "deny";
	const res = await apiFetch(`/api/auth/cli/requests/${encodeURIComponent(userCode)}/${action}`, {
		method: "POST",
	});
	if (!res.ok)
		throw new Error(await apiErrorMessage(res, "ログインリクエストの処理に失敗しました"));
}

/** バックエンド RoadmapTaskDto に対応。日付は "YYYY-MM-DD" で両端を含む。 */
export interface RoadmapTask {
	id: string;
	name: string;
	description: string;
	assigneeUserId: string | null;
	startDate: string;
	endDate: string;
	done: boolean;
}

/** バックエンド RoadmapAssigneeDto に対応。無効なユーザーはタスクが残っているときだけ含まれ、新しくは割り当てられない。 */
export interface RoadmapAssignee {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	active: boolean;
}

export type RoadmapTaskInput = Omit<RoadmapTask, "id">;

/** GET /api/knowledge-graph — 組織のナレッジグラフの記事とリンクを取得する。 */
export async function getKnowledgeGraph(): Promise<KnowledgeGraph> {
	const res = await apiFetch("/api/knowledge-graph");
	if (!res.ok) throw new Error(`ナレッジグラフの取得に失敗しました (${res.status})`);
	return (await res.json()) as KnowledgeGraph;
}

/** GET /api/knowledge-graph/articles/by-name — 組織の記事を名前で引く。無ければ null。 */
export async function getKnowledgeArticleByName(name: string): Promise<KnowledgeGraphNode | null> {
	const res = await apiFetch(
		`/api/knowledge-graph/articles/by-name?name=${encodeURIComponent(name)}`,
	);
	if (res.status === 404) return null;
	if (!res.ok) throw new Error(`記事の取得に失敗しました (${res.status})`);
	return (await res.json()) as KnowledgeGraphNode;
}

/** GET /api/roadmap/tasks — ロードマップの全タスクを作成順に取得する。 */
export async function getRoadmapTasks(): Promise<RoadmapTask[]> {
	const res = await apiFetch("/api/roadmap/tasks");
	if (!res.ok) throw new Error(`ロードマップの取得に失敗しました (${res.status})`);
	return (await res.json()) as RoadmapTask[];
}

/** GET /api/roadmap/assignees — レーンになるユーザーを名前順に取得する。 */
export async function getRoadmapAssignees(): Promise<RoadmapAssignee[]> {
	const res = await apiFetch("/api/roadmap/assignees");
	if (!res.ok) throw new Error(`担当者の取得に失敗しました (${res.status})`);
	return (await res.json()) as RoadmapAssignee[];
}

/** POST /api/roadmap/tasks — タスクを作成する。 */
export async function createRoadmapTask(input: RoadmapTaskInput): Promise<RoadmapTask> {
	const res = await apiFetch("/api/roadmap/tasks", { method: "POST", body: JSON.stringify(input) });
	if (!res.ok) throw new Error(await apiErrorMessage(res, "タスクの作成に失敗しました"));
	return (await res.json()) as RoadmapTask;
}

/** PUT /api/roadmap/tasks/{id} — タスクの全項目を置き換える。 */
export async function updateRoadmapTask(id: string, input: RoadmapTaskInput): Promise<RoadmapTask> {
	const res = await apiFetch(`/api/roadmap/tasks/${encodeURIComponent(id)}`, {
		method: "PUT",
		body: JSON.stringify(input),
	});
	if (!res.ok) throw new Error(await apiErrorMessage(res, "タスクの保存に失敗しました"));
	return (await res.json()) as RoadmapTask;
}

/** DELETE /api/roadmap/tasks/{id} — タスクを削除する。 */
export async function deleteRoadmapTask(id: string): Promise<void> {
	const res = await apiFetch(`/api/roadmap/tasks/${encodeURIComponent(id)}`, { method: "DELETE" });
	if (!res.ok) throw new Error(`タスクの削除に失敗しました (${res.status})`);
}
